import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchFile } from "@ffmpeg/util";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { uploadFile } from "../../lib/upload";
import { getFFmpeg } from "../../lib/ffmpeg-loader";
import StudioOnboarding, { hasSeenStudioTour, resetStudioTour } from "./StudioOnboarding";
import Timeline from "./Timeline";
import AudioLanes from "./AudioLanes";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Slider } from "../ui/slider";
import {
  X, Scissors, Crop, Sparkles, Type, Volume2, VolumeX, Wand2, Captions,
  Save, Download, Loader2, RotateCcw, Zap, HelpCircle, Smile, LayoutTemplate,
  Copy, ArrowRight, Trash2, RotateCw, FlipHorizontal, FlipVertical, Rewind, Split, Blend, Repeat, MessageCircle, Music, Mic,
} from "lucide-react";

// ---------------- Constants ----------------
const MAX_VIDEO_SEC = 60;

const ASPECTS = [
  { v: "original", l: "Free", ratio: null },
  { v: "9:16", l: "9:16", ratio: 9 / 16 },
  { v: "1:1", l: "1:1", ratio: 1 },
  { v: "4:5", l: "4:5", ratio: 4 / 5 },
  { v: "16:9", l: "16:9", ratio: 16 / 9 },
];

const FILTER_PRESETS = [
  { v: "none",     l: "Original",     css: "",                                                     eq: null },
  { v: "vivid",    l: "Vivid",        css: "saturate(1.4) contrast(1.08)",                         eq: "eq=saturation=1.4:contrast=1.08" },
  { v: "vintage",  l: "Vintage",      css: "sepia(0.35) saturate(0.9) contrast(1.05)",             eq: "eq=saturation=0.7:contrast=1.05,curves=preset=vintage" },
  { v: "warm",     l: "Warm",         css: "saturate(1.15) hue-rotate(-8deg) brightness(1.03)",    eq: "eq=saturation=1.15:brightness=0.02" },
  { v: "cool",     l: "Cool",         css: "saturate(1.05) hue-rotate(8deg) brightness(0.98)",     eq: "eq=saturation=1.05:brightness=-0.02" },
  { v: "golden",   l: "Golden",       css: "saturate(1.2) hue-rotate(-15deg) brightness(1.06) contrast(1.05)", eq: "eq=saturation=1.2:brightness=0.04:contrast=1.05" },
  { v: "neon",     l: "Neon",         css: "saturate(1.9) contrast(1.15) brightness(0.95)",        eq: "eq=saturation=1.9:contrast=1.15:brightness=-0.02" },
  { v: "fade",     l: "Fade",         css: "saturate(0.85) contrast(0.9) brightness(1.05)",        eq: "eq=saturation=0.85:contrast=0.9:brightness=0.04" },
  { v: "mono",     l: "Mono",         css: "grayscale(1) contrast(1.05)",                          eq: "hue=s=0,eq=contrast=1.05" },
  { v: "portrait", l: "Portrait",     css: "saturate(1.05) contrast(1.02) brightness(1.02)",       eq: "eq=saturation=1.05:contrast=1.02:brightness=0.02" },
  { v: "cinema",   l: "Cinema",       css: "saturate(0.9) contrast(1.15) brightness(0.95)",        eq: "eq=saturation=0.9:contrast=1.15:brightness=-0.03" },
  { v: "cyber",    l: "Cyber",        css: "saturate(1.6) hue-rotate(12deg) contrast(1.1)",        eq: "eq=saturation=1.6:contrast=1.1" },
  { v: "sunset",   l: "Sunset",       css: "saturate(1.25) hue-rotate(-25deg) brightness(1.05)",   eq: "eq=saturation=1.25:brightness=0.03" },
  { v: "retro",    l: "Retro",        css: "sepia(0.5) saturate(1.2) contrast(1.1)",               eq: "eq=saturation=0.6:contrast=1.1,curves=preset=vintage" },
  { v: "duotone",  l: "Duotone",      css: "grayscale(1) contrast(1.15) sepia(0.6) hue-rotate(180deg) saturate(2)", eq: "hue=s=0,eq=contrast=1.15,curves=preset=cross_process" },
  { v: "bloom",    l: "Bloom",        css: "saturate(1.3) brightness(1.08) blur(0.3px)",           eq: "eq=saturation=1.3:brightness=0.05" },
  { v: "punch",    l: "Punch",        css: "saturate(1.6) contrast(1.25) brightness(1.02)",        eq: "eq=saturation=1.6:contrast=1.25:brightness=0.02" },
  { v: "frost",    l: "Frost",        css: "saturate(0.6) hue-rotate(10deg) brightness(1.05) contrast(0.95)", eq: "eq=saturation=0.6:brightness=0.03:contrast=0.95" },
  { v: "sepia",    l: "Sepia",        css: "sepia(0.85) saturate(1.1)",                             eq: "colorchannelmixer=.393:.769:.189:0:.349:.686:.168:0:.272:.534:.131" },
  { v: "matrix",   l: "Matrix",       css: "hue-rotate(90deg) saturate(1.5) contrast(1.15)",       eq: "hue=h=90,eq=saturation=1.5:contrast=1.15" },
];

const STICKERS = [
  "🔥","✨","💫","💎","👑","💖","💯","⚡","🌈","🎉","🎊","🎁",
  "☕","🍕","🌟","💥","🚀","🎯","💪","🥇","😍","😎","🤩","🥰",
];

const TEXT_ANIMATIONS = [
  { v: "static",     l: "Static",     desc: "No animation" },
  { v: "fade",       l: "Fade in",    desc: "Soft fade-up entrance" },
  { v: "pop",        l: "Pop",        desc: "Scale bounce" },
  { v: "slide",      l: "Slide up",   desc: "Slide from below" },
  { v: "bounce",     l: "Bounce",     desc: "Playful spring bounce" },
  { v: "typewriter", l: "Typewriter", desc: "Letter-by-letter reveal" },
  { v: "glow",       l: "Glow pulse", desc: "Neon pulse loop" },
  { v: "wave",       l: "Wave",       desc: "Per-letter wave" },
];

const TEXT_COLORS = ["#FFFFFF", "#FF2F6E", "#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#000000"];

// Template presets — one tap, sets multiple values.
const TEMPLATES = [
  { v: "product-reveal", l: "Product reveal", aspect: "9:16", filter: "vivid",    overlay: "New drop 🔥", pos: "top",    anim: "pop" },
  { v: "before-after",   l: "Before → After", aspect: "1:1",  filter: "portrait", overlay: "Before → After", pos: "bottom", anim: "slide" },
  { v: "pov",            l: "POV",            aspect: "9:16", filter: "cinema",   overlay: "POV: You just...", pos: "top",  anim: "fade" },
  { v: "tutorial",       l: "Tutorial",       aspect: "9:16", filter: "portrait", overlay: "Step 01",       pos: "top",    anim: "fade" },
  { v: "unboxing",       l: "Unboxing",       aspect: "9:16", filter: "warm",     overlay: "It's here 🎁",  pos: "middle", anim: "pop" },
  { v: "testimonial",    l: "Testimonial",    aspect: "1:1",  filter: "portrait", overlay: "5-star vibes", pos: "bottom",  anim: "fade" },
];

// ---------------- Helpers ----------------
function bytesToMB(b) { return (b / (1024 * 1024)).toFixed(1); }

function timeAgo(iso) {
  if (!iso) return "just now";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function useObjectURL(file) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!file) { setUrl(""); return; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

/** Media editor modal — Edits-inspired UI. Fullscreen dark canvas + icon dock. */
export default function MediaEditor({ file: fileProp, onCancel, onExport, draftId = null, onDraftSaved, initialTemplate = null, autoClipMeta = null, hireId = null }) {
  // primaryOverride lets us swap the Main clip in-place without touching the parent File prop.
  // While overridden, `file`/`url` below reflect the swapped clip.
  const [primaryOverride, setPrimaryOverride] = useState(null); // {file, url, duration}
  const file = primaryOverride?.file || fileProp;
  const propUrl = useObjectURL(fileProp);
  const url = primaryOverride?.url || propUrl;
  const kind = (file?.type || "").startsWith("video") ? "video" : "image";

  // ------------- Media meta -------------
  const [duration, setDuration] = useState(0);
  const [naturalW, setNaturalW] = useState(0);
  const [naturalH, setNaturalH] = useState(0);

  // ------------- Edit state -------------
  const [aspect, setAspect] = useState("original");
  const [filter, setFilter] = useState("none");
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(1);
  const [saturation, setSaturation] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [muted, setMuted] = useState(false);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(15);
  const [textOverlay, setTextOverlay] = useState("");
  const [textPos, setTextPos] = useState("bottom");
  // Percentage coords for text overlay (0-100). Independent of Top/Mid/Btm presets.
  // The presets set this, but the user can drag text freely to anywhere on the canvas.
  const [textPosPct, setTextPosPct] = useState({ x: 50, y: 90 });
  const [textScale, setTextScale] = useState(1);    // 0.4 – 3
  const [textRotate, setTextRotate] = useState(0);  // degrees, -180..180
  const [textBg, setTextBg] = useState("none");     // 'none' | 'solid' | 'semi' | 'marker' | 'pill'
  const [textBgColor, setTextBgColor] = useState("#000000");
  const [textDragging, setTextDragging] = useState(false);
  const [selectedStickerId, setSelectedStickerId] = useState(null);
  // Multi-layer text: previously "committed" text layers, drawn behind the currently-edited overlay.
  // Each: { id, content, xPct, yPct, scale, rotate, anim, color }
  const [committedTexts, setCommittedTexts] = useState([]);
  const [editingCommittedId, setEditingCommittedId] = useState(null); // id if we're editing a previously-committed layer
  const [textAnim, setTextAnim] = useState("static");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [stickers, setStickers] = useState([]); // [{id, type, emoji|text+shape+color, xPct, yPct, size}]
  const [textStickerText, setTextStickerText] = useState("");
  const [textStickerShape, setTextStickerShape] = useState("chip");
  const [textStickerColor, setTextStickerColor] = useState("#FF2F6E");
  const [rotate, setRotate] = useState(0);        // 0 | 90 | 180 | 270
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [reverse, setReverse] = useState(false);  // video only
  // ---- Green screen / chroma key state ----
  const [chromaEnabled, setChromaEnabled] = useState(false);
  const [chromaColor, setChromaColor] = useState("#00FF00"); // key colour to remove
  const [chromaSimilarity, setChromaSimilarity] = useState(0.3); // 0.01–0.5 → higher = more colour range removed
  const [chromaBlend, setChromaBlend] = useState(0.1); // 0–0.3 → edge softness
  const [chromaBgColor, setChromaBgColor] = useState("#111111"); // fallback background
  const [volume, setVolume] = useState(1);        // 0..2 (2x boost)
  const [fadeIn, setFadeIn] = useState(0);        // seconds (0 = off)
  const [fadeOut, setFadeOut] = useState(0);
  const [splits, setSplits] = useState([]);       // array of cut times (seconds, sorted)
  // Multi-clip (Phase 3 MVP): additional clips appended after the primary one.
  // On export, they're concatenated via ffmpeg's concat filter.
  const [extraClips, setExtraClips] = useState([]); // [{id, file, url, duration, trimStart, trimEnd, muted}]
  const [activeClipId, setActiveClipId] = useState("primary");
  // Transitions between adjacent clips: index N is the transition AFTER clip N.
  // Length = (1 + extraClips.length) - 1
  const [transitions, setTransitions] = useState([]); // ["none"|"fade"|"dissolve"|"wipe", ...]
  const [dragClipId, setDragClipId] = useState(null);
  const [clipAudioPopover, setClipAudioPopover] = useState(null); // clip id or null
  // Playback loop for multi-clip sequence — declared here (before effects that reference it)
  // to avoid a temporal dead zone when React initialises the component.
  const [loopPlayback, setLoopPlayback] = useState(false);
  const extraClipsRef = useRef([]);
  const clipFileInputRef = useRef(null);
  useEffect(() => { extraClipsRef.current = extraClips; }, [extraClips]);
  useEffect(() => () => { extraClipsRef.current.forEach((c) => c.url && URL.revokeObjectURL(c.url)); }, []);
  // Keep transitions array in sync with clip count
  useEffect(() => {
    const wanted = Math.max(0, extraClips.length);
    setTransitions((prev) => {
      const next = prev.slice(0, wanted);
      while (next.length < wanted) next.push("none");
      return next;
    });
  }, [extraClips.length]);

  // Auto-seek to the active clip's trimStart when the clip changes so playback
  // starts from the trim in-point (not raw source 0). Works across primary + extras.
  useEffect(() => {
    if (kind !== "video") return;
    const v = videoRef.current;
    if (!v) return;
    // Resolve trimStart inline (activeTrimStart const is declared later in the
    // component body — referencing it here would trigger a TDZ error).
    const currentTrimStart = activeClipId === "primary"
      ? trimStart
      : (extraClips.find((c) => c.id === activeClipId)?.trimStart || 0);
    const seekIn = () => {
      try { v.currentTime = Math.max(0, currentTrimStart || 0); } catch { /* noop */ }
    };
    if (v.readyState >= 1) seekIn();
    else v.addEventListener("loadedmetadata", seekIn, { once: true });
    return () => v.removeEventListener("loadedmetadata", seekIn);
  }, [activeClipId, extraClips, trimStart, kind]);

  // Multi-clip playback sync: when the active clip reaches trimEnd, auto-advance
  // to the next clip in the sequence (primary → extras[0] → extras[1] …) and keep
  // playing so the user gets a seamless preview of the whole edit.
  useEffect(() => {
    if (kind !== "video") return;
    const v = videoRef.current;
    if (!v) return;
    const totalClips = 1 + extraClips.length;
    if (totalClips <= 1) return;
    const currentTrimEnd = activeClipId === "primary"
      ? trimEnd
      : (extraClips.find((c) => c.id === activeClipId)?.trimEnd || 0);

    const onTimeUpdate = () => {
      if (v.paused || v.seeking) return;
      if (!isFinite(currentTrimEnd) || currentTrimEnd <= 0) return;
      if (v.currentTime + 0.05 < currentTrimEnd) return;
      // Reached the trim out-point: advance
      const seq = ["primary", ...extraClips.map((c) => c.id)];
      const idx = seq.indexOf(activeClipId);
      const nextIdx = idx + 1;
      v.pause();
      const doAdvance = (nextId, nextStart) => {
        setActiveClipId(nextId);
        requestAnimationFrame(() => {
          const vv = videoRef.current;
          if (!vv) return;
          const resume = () => {
            try { vv.currentTime = nextStart; } catch { /* noop */ }
            vv.play().catch(() => {});
          };
          if (vv.readyState >= 1) resume();
          else vv.addEventListener("loadedmetadata", resume, { once: true });
        });
      };
      if (nextIdx < seq.length) {
        const nextId = seq[nextIdx];
        const nextStart = nextId === "primary" ? trimStart : extraClips.find((c) => c.id === nextId)?.trimStart || 0;
        doAdvance(nextId, nextStart);
      } else if (loopPlayback) {
        // Loop back to primary
        doAdvance("primary", trimStart);
      }
    };

    v.addEventListener("timeupdate", onTimeUpdate);
    return () => v.removeEventListener("timeupdate", onTimeUpdate);
  }, [activeClipId, extraClips, trimEnd, trimStart, kind, loopPlayback]);

  // ------------- UI state -------------
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeTool, setActiveTool] = useState(null); // null | trim | crop | look | text | sticker | template | audio | ai
  const [showTour, setShowTour] = useState(false);
  const [savedSourceUrl, setSavedSourceUrl] = useState(null);

  useEffect(() => { if (!hasSeenStudioTour()) setShowTour(true); }, []);

  // Apply an initial template (Studio "New from template" flow) exactly once on mount
  const initialTemplateAppliedRef = useRef(false);
  useEffect(() => {
    if (!initialTemplate || initialTemplateAppliedRef.current) return;
    initialTemplateAppliedRef.current = true;
    const t = initialTemplate;
    if (t.aspect) setAspect(t.aspect);
    if (t.filter) setFilter(t.filter);
    if (t.textOverlay) setTextOverlay(t.textOverlay);
    if (t.textPos) {
      setTextPos(t.textPos);
      setTextPosPct(t.textPos === "top" ? { x: 50, y: 12 } : t.textPos === "middle" ? { x: 50, y: 50 } : { x: 50, y: 90 });
    }
    if (t.textAnim) setTextAnim(t.textAnim);
    if (t.textColor) setTextColor(t.textColor);
    if (t.title) toast.success(`${t.title} template applied`);
    setActiveTool("text");
    api.post("/studio/analytics/event", { event: "template_applied", payload: { template: t.id || t.title || "custom" } }).catch(() => {});
  }, [initialTemplate]);

  // Auto-clip meta: pre-trim the source to the picked window once duration is known
  const autoClipAppliedRef = useRef(false);
  useEffect(() => {
    if (!autoClipMeta || autoClipAppliedRef.current) return;
    if (!duration) return; // wait for video metadata
    autoClipAppliedRef.current = true;
    const s = Math.max(0, Math.min(autoClipMeta.startS || 0, duration - 1));
    const e = Math.max(s + 1, Math.min(autoClipMeta.endS || (s + 15), duration));
    setTrimStart(s);
    setTrimEnd(e);
    if (autoClipMeta.hook) setTextOverlay(autoClipMeta.hook.slice(0, 32));
    if (autoClipMeta.caption) setCaptionContext(autoClipMeta.caption.slice(0, 200));
    toast.success(`Auto clip loaded · ${(e - s).toFixed(0)}s window`);
  }, [autoClipMeta, duration]);

  // ------------- AI -------------
  const [caption, setCaption] = useState(null);
  const [captionBusy, setCaptionBusy] = useState(false);
  const [captionContext, setCaptionContext] = useState("");
  const [subsBusy, setSubsBusy] = useState(false);
  const [subs, setSubs] = useState(null);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestResult, setSuggestResult] = useState(null); // { vibe, appliedTo, count }
  const [refinePrompt, setRefinePrompt] = useState("");
  const [autoEditBusy, setAutoEditBusy] = useState(false);
  const [autoEditResult, setAutoEditResult] = useState(null); // { vibe, applied }

  // ------------- Music track (background audio) -------------
  const [musicFile, setMusicFile] = useState(null); // File
  const [musicUrl, setMusicUrl] = useState(""); // objectURL for preview
  const [musicName, setMusicName] = useState("");
  const [musicVolume, setMusicVolume] = useState(0.7); // 0–2
  const [musicStart, setMusicStart] = useState(0); // start offset in the music track, in seconds
  const [musicFadeIn, setMusicFadeIn] = useState(0.5);
  const [musicFadeOut, setMusicFadeOut] = useState(0.8);
  const [musicDuck, setMusicDuck] = useState(true); // duck original audio to 30% when music is present
  const musicAudioRef = useRef(null);
  const musicFileInputRef = useRef(null);
  const [musicPreviewing, setMusicPreviewing] = useState(false);
  const [musicLibrary, setMusicLibrary] = useState([]);
  const [musicTab, setMusicTab] = useState("library"); // "library" | "pro" | "upload"
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryPickingId, setLibraryPickingId] = useState(null);
  // Pro music (Epidemic Sound)
  const [proAvailable, setProAvailable] = useState(false);
  const [proTracks, setProTracks] = useState([]);
  const [proMoods, setProMoods] = useState([]);
  const [proSearchTerm, setProSearchTerm] = useState("");
  const [proSelectedMood, setProSelectedMood] = useState(null);
  const [proLoading, setProLoading] = useState(false);
  const [proPickingId, setProPickingId] = useState(null);
  const proPreviewRef = useRef(null);
  const [proPreviewingId, setProPreviewingId] = useState(null);
  useEffect(() => {
    // Check whether the server has Pro Music configured
    api.get("/studio/music/pro/status").then((r) => setProAvailable(!!r.data?.available)).catch(() => {});
  }, []);
  useEffect(() => () => { if (musicUrl) URL.revokeObjectURL(musicUrl); }, [musicUrl]);

  // ------------- Voiceover track (multi-track audio) -------------
  const [voiceoverFile, setVoiceoverFile] = useState(null);
  const [voiceoverUrl, setVoiceoverUrl] = useState("");
  const [voiceoverName, setVoiceoverName] = useState("");
  const [voiceoverVolume, setVoiceoverVolume] = useState(1);
  const [voiceoverStart, setVoiceoverStart] = useState(0); // start in the output timeline (seconds)
  const voiceoverAudioRef = useRef(null);
  const voiceoverFileInputRef = useRef(null);
  const [voiceoverPreviewing, setVoiceoverPreviewing] = useState(false);
  useEffect(() => () => { if (voiceoverUrl) URL.revokeObjectURL(voiceoverUrl); }, [voiceoverUrl]);

  // ------------- Sound FX (short punctuation clips) -------------
  const [fxLibrary, setFxLibrary] = useState([]);
  const [fxLoading, setFxLoading] = useState(false);
  const [fxPickingId, setFxPickingId] = useState(null);
  const [fxSlots, setFxSlots] = useState([]); // [{id, title, file, url, atSec, volume}]
  const fxAudioPreviewRef = useRef(null);
  useEffect(() => () => { fxSlots.forEach((s) => { if (s.url) URL.revokeObjectURL(s.url); }); }, []);

  // ------------- Beat sync -------------
  const [activeMusicTrackId, setActiveMusicTrackId] = useState(null);
  const [beatSyncing, setBeatSyncing] = useState(false);

  // ------------- Freemium quota + watermark -------------
  const [quota, setQuota] = useState(null); // { used, limit, remaining, is_subscribed, watermark_required, can_export }
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  useEffect(() => { api.get("/studio/quota").then((r) => setQuota(r.data)).catch(() => {}); }, []);

  // ------------- Draft auto-recovery -------------
  // When the editor opens WITHOUT an explicit draftId, check for a very recent
  // auto-saved draft (< 24h old) and offer to restore it. Only offers once per session.
  const [recoverableDraft, setRecoverableDraft] = useState(null);
  const [recoveryChecked, setRecoveryChecked] = useState(false);
  useEffect(() => {
    if (draftId || recoveryChecked) return;
    setRecoveryChecked(true);
    api.get("/studio/drafts").then((r) => {
      const drafts = r.data?.drafts || [];
      const now = Date.now();
      const recent = drafts.find((d) => {
        const updated = new Date(d.updated_at).getTime();
        return (now - updated) < 24 * 60 * 60 * 1000;
      });
      if (recent) setRecoverableDraft(recent);
    }).catch(() => {});
  }, [draftId, recoveryChecked]);

  // ------------- Auto-save state -------------
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const autoSaveTimerRef = useRef(null);

  const videoRef = useRef(null);
  const imgRef = useRef(null);
  const previewRef = useRef(null);

  const onVideoLoaded = () => {
    const v = videoRef.current;
    if (!v) return;
    // Only initialise primary clip trim from video metadata — extras keep their own trim
    const d = isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
    if (isActivePrimary && duration === 0) {
      if (d > 0) {
        setDuration(d);
        setTrimStart(0);
        setTrimEnd(Math.min(MAX_VIDEO_SEC, d));
      }
      setNaturalW(v.videoWidth || 0);
      setNaturalH(v.videoHeight || 0);
    } else if (isActivePrimary) {
      setNaturalW(v.videoWidth || 0);
      setNaturalH(v.videoHeight || 0);
    }
  };
  // Fallback for browsers/blobs that report duration late (Infinity/NaN on loadedmetadata).
  // Fires on durationchange and loadeddata to catch synthetic/streaming video sources.
  const onDurationOrDataChange = () => {
    const v = videoRef.current;
    if (!v || !isActivePrimary) return;
    const d = isFinite(v.duration) && v.duration > 0 ? v.duration : 0;
    if (d > 0 && duration === 0) {
      setDuration(d);
      setTrimStart(0);
      setTrimEnd(Math.min(MAX_VIDEO_SEC, d));
    }
    if (v.videoWidth && !naturalW) setNaturalW(v.videoWidth);
    if (v.videoHeight && !naturalH) setNaturalH(v.videoHeight);
  };
  const onImageLoaded = () => {
    const i = imgRef.current;
    if (!i) return;
    setNaturalW(i.naturalWidth || 0);
    setNaturalH(i.naturalHeight || 0);
  };

  // Probe a video File → returns { duration } via off-screen <video>
  const probeVideoDuration = (f) => new Promise((resolve) => {
    const el = document.createElement("video");
    el.preload = "metadata";
    el.muted = true;
    el.src = URL.createObjectURL(f);
    el.onloadedmetadata = () => {
      const d = el.duration || 0;
      URL.revokeObjectURL(el.src);
      resolve(d);
    };
    el.onerror = () => resolve(0);
  });

  const addClipFile = async (f) => {
    if (!f || !f.type?.startsWith("video")) { toast.error("Add a video clip"); return; }
    // Enforce total 15s budget across all clips
    const currentTotal = (trimEnd - trimStart) + extraClips.reduce((acc, c) => acc + (c.trimEnd - c.trimStart), 0);
    if (currentTotal >= MAX_VIDEO_SEC) { toast.error(`Total edit already at ${MAX_VIDEO_SEC}s cap`); return; }
    const d = await probeVideoDuration(f);
    const remaining = MAX_VIDEO_SEC - currentTotal;
    const end = Math.min(d || remaining, remaining);
    const clip = {
      id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      file: f,
      url: URL.createObjectURL(f),
      duration: d,
      trimStart: 0,
      trimEnd: end,
      muted: false,
      volume: 1,
      filter: "none",
    };
    setExtraClips((prev) => [...prev, clip]);
    toast.success(`Clip added · ${end.toFixed(1)}s`);
  };

  const removeExtraClip = (id) => {
    setExtraClips((prev) => {
      const c = prev.find((x) => x.id === id);
      // Only revoke the URL if no other clip (primary or extra) still references it — split segments share.
      const stillUsed = prev.some((x) => x.id !== id && x.url === c?.url);
      if (c?.url && !stillUsed && c.url !== url) URL.revokeObjectURL(c.url);
      return prev.filter((x) => x.id !== id);
    });
  };

  // ------------- TikTok-style split at playhead -------------
  // Splits the currently-active clip at the video's currentTime, keeping BOTH halves
  // as independent segments in the clip strip.
  const splitAtPlayhead = () => {
    const t = Number((videoRef.current?.currentTime || 0).toFixed(3));
    const genId = () => `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    if (activeClipId === "primary") {
      if (t <= trimStart + 0.05 || t >= trimEnd - 0.05) {
        toast.error("Move the playhead further into the clip, then split");
        return;
      }
      const newSeg = {
        id: genId(),
        file: null,           // shared with primary
        url,                  // share the primary's object URL
        duration,
        trimStart: t,
        trimEnd,
        muted,
        volume,
        filter,               // inherit primary's filter
      };
      // Shrink primary to [trimStart, t]
      setTrimEnd(t);
      // Insert new segment at the FRONT of extras so it plays right after primary
      setExtraClips((prev) => [newSeg, ...prev]);
      setTransitions((prev) => ["none", ...prev]);
      toast.success(`Split · ${(trimEnd - t).toFixed(1)}s moved to a new segment`);
      return;
    }
    const idx = extraClips.findIndex((c) => c.id === activeClipId);
    if (idx === -1) return;
    const clip = extraClips[idx];
    if (t <= clip.trimStart + 0.05 || t >= clip.trimEnd - 0.05) {
      toast.error("Move the playhead further into the clip, then split");
      return;
    }
    const newSeg = {
      id: genId(),
      file: null,
      url: clip.url,
      duration: clip.duration,
      trimStart: t,
      trimEnd: clip.trimEnd,
      muted: clip.muted,
      volume: clip.volume,
      filter: clip.filter,
    };
    setExtraClips((prev) => {
      const next = [...prev];
      next[idx] = { ...clip, trimEnd: t };
      next.splice(idx + 1, 0, newSeg);
      return next;
    });
    setTransitions((prev) => {
      const next = [...prev];
      next.splice(idx + 1, 0, "none");
      return next;
    });
    toast.success(`Split at ${t.toFixed(2)}s — new segment created`);
  };

  const totalEditDuration = () =>
    Math.max(0, trimEnd - trimStart) + extraClips.reduce((a, c) => a + Math.max(0, c.trimEnd - c.trimStart), 0);

  // Active clip selection — powers the Timeline + video preview src for per-clip trim
  const isActivePrimary = activeClipId === "primary";
  const activeExtra = extraClips.find((c) => c.id === activeClipId);
  const activeSrc = isActivePrimary ? url : (activeExtra?.url || url);
  const activeDuration = isActivePrimary ? duration : (activeExtra?.duration || 0);
  const activeTrimStart = isActivePrimary ? trimStart : (activeExtra?.trimStart || 0);
  const activeTrimEnd = isActivePrimary ? trimEnd : (activeExtra?.trimEnd || 0);
  const setActiveTrimStart = (v) => {
    if (isActivePrimary) setTrimStart(v);
    else setExtraClips((prev) => prev.map((c) => c.id === activeClipId ? { ...c, trimStart: v } : c));
  };
  const setActiveTrimEnd = (v) => {
    if (isActivePrimary) setTrimEnd(v);
    else setExtraClips((prev) => prev.map((c) => c.id === activeClipId ? { ...c, trimEnd: v } : c));
  };
  // Per-clip filter routing — the Look panel and preview both read/write this.
  const activeFilter = isActivePrimary ? filter : (activeExtra?.filter || "none");
  const setActiveFilter = (v) => {
    if (isActivePrimary) setFilter(v);
    else setExtraClips((prev) => prev.map((c) => c.id === activeClipId ? { ...c, filter: v } : c));
  };
  const filterLabel = (v) => FILTER_PRESETS.find((f) => f.v === v)?.l || "Filter";

  const cycleTransition = (idx) => {
    const order = ["none", "fade", "dissolve", "wipe", "slideleft", "slideright", "circleopen", "radial", "fadeblack"];
    setTransitions((prev) => {
      const nextValue = order[(order.indexOf(prev[idx] || "none") + 1) % order.length];
      api.post("/studio/analytics/event", { event: "transition_applied", payload: { transition: nextValue } }).catch(() => {});
      return prev.map((t, i) => i === idx ? nextValue : t);
    });
  };
  const transitionIcon = (t) => {
    const m = { none: "|", fade: "◐", dissolve: "◈", wipe: "▶", slideleft: "◀", slideright: "▶", circleopen: "◯", radial: "☀", fadeblack: "●" };
    return m[t] || "|";
  };
  const transitionLabel = (t) => t === "none" ? "Cut" : t === "slideleft" ? "Slide L" : t === "slideright" ? "Slide R" : t === "circleopen" ? "Circle" : t === "radial" ? "Radial" : t === "fadeblack" ? "Fade blk" : t.charAt(0).toUpperCase() + t.slice(1);

  // Reorder via HTML5 drag & drop — swap-based. Now supports Main ↔ extra swaps.
  const onPillDrop = (targetId) => {
    if (!dragClipId || dragClipId === targetId) { setDragClipId(null); return; }
    // Swap Main ↔ extra
    if (dragClipId === "primary" || targetId === "primary") {
      const extraId = dragClipId === "primary" ? targetId : dragClipId;
      const other = extraClips.find((c) => c.id === extraId);
      if (!other) { setDragClipId(null); return; }
      const oldPrimary = {
        id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        file, url,
        duration,
        trimStart, trimEnd,
        muted, volume,
        filter,
      };
      setPrimaryOverride({ file: other.file, url: other.url, duration: other.duration });
      setDuration(other.duration);
      setTrimStart(other.trimStart);
      setTrimEnd(other.trimEnd);
      setMuted(!!other.muted);
      setVolume(typeof other.volume === "number" ? other.volume : 1);
      setFilter(other.filter || "none");
      setExtraClips((prev) => prev.map((c) => c.id === extraId ? oldPrimary : c));
      setActiveClipId("primary");
      toast.success("Main clip swapped");
      setDragClipId(null);
      return;
    }
    // Swap extra ↔ extra
    setExtraClips((prev) => {
      const a = prev.findIndex((c) => c.id === dragClipId);
      const b = prev.findIndex((c) => c.id === targetId);
      if (a < 0 || b < 0) return prev;
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
    setDragClipId(null);
  };

  // Per-clip audio setters
  const setExtraMuted = (id, muted) => setExtraClips((prev) => prev.map((c) => c.id === id ? { ...c, muted } : c));
  const setExtraVolume = (id, volume) => setExtraClips((prev) => prev.map((c) => c.id === id ? { ...c, volume } : c));

  // Load draft
  useEffect(() => {
    if (!draftId) return;
    api.get(`/studio/drafts/${draftId}`).then((r) => {
      const s = r.data?.edit_state || {};
      if (s.aspect) setAspect(s.aspect);
      if (s.filter) setFilter(s.filter);
      if (typeof s.brightness === "number") setBrightness(s.brightness);
      if (typeof s.contrast === "number") setContrast(s.contrast);
      if (typeof s.saturation === "number") setSaturation(s.saturation);
      if (typeof s.speed === "number") setSpeed(s.speed);
      if (typeof s.muted === "boolean") setMuted(s.muted);
      if (typeof s.trimStart === "number") setTrimStart(s.trimStart);
      if (typeof s.trimEnd === "number") setTrimEnd(s.trimEnd);
      if (typeof s.textOverlay === "string") setTextOverlay(s.textOverlay);
      if (s.textPos) setTextPos(s.textPos);
      if (s.textPosPct && typeof s.textPosPct.x === "number" && typeof s.textPosPct.y === "number") {
        setTextPosPct({ x: s.textPosPct.x, y: s.textPosPct.y });
      } else if (s.textPos) {
        // Legacy drafts — derive pct coords from the preset
        setTextPosPct(s.textPos === "top" ? { x: 50, y: 12 } : s.textPos === "middle" ? { x: 50, y: 50 } : { x: 50, y: 90 });
      }
      if (typeof s.textScale === "number") setTextScale(s.textScale);
      if (typeof s.textRotate === "number") setTextRotate(s.textRotate);
      if (s.textBg) setTextBg(s.textBg);
      if (s.textBgColor) setTextBgColor(s.textBgColor);
      if (Array.isArray(s.committedTexts)) setCommittedTexts(s.committedTexts);
      if (s.textAnim) setTextAnim(s.textAnim);
      if (s.textColor) setTextColor(s.textColor);
      if (Array.isArray(s.stickers)) setStickers(s.stickers);
      if (typeof s.rotate === "number") setRotate(s.rotate);
      if (typeof s.flipH === "boolean") setFlipH(s.flipH);
      if (typeof s.flipV === "boolean") setFlipV(s.flipV);
      if (typeof s.reverse === "boolean") setReverse(s.reverse);
      if (typeof s.volume === "number") setVolume(s.volume);
      if (typeof s.fadeIn === "number") setFadeIn(s.fadeIn);
      if (typeof s.fadeOut === "number") setFadeOut(s.fadeOut);
      if (Array.isArray(s.splits)) setSplits(s.splits);
      // Chroma key
      if (typeof s.chromaEnabled === "boolean") setChromaEnabled(s.chromaEnabled);
      if (typeof s.chromaColor === "string") setChromaColor(s.chromaColor);
      if (typeof s.chromaSimilarity === "number") setChromaSimilarity(s.chromaSimilarity);
      if (typeof s.chromaBlend === "number") setChromaBlend(s.chromaBlend);
      if (typeof s.chromaBgColor === "string") setChromaBgColor(s.chromaBgColor);
      // Music metadata is persisted (settings) but the actual audio file must be re-uploaded per session
      if (typeof s.musicVolume === "number") setMusicVolume(s.musicVolume);
      if (typeof s.musicStart === "number") setMusicStart(s.musicStart);
      if (typeof s.musicFadeIn === "number") setMusicFadeIn(s.musicFadeIn);
      if (typeof s.musicFadeOut === "number") setMusicFadeOut(s.musicFadeOut);
      if (typeof s.musicDuck === "boolean") setMusicDuck(s.musicDuck);
      if (typeof s.voiceoverVolume === "number") setVoiceoverVolume(s.voiceoverVolume);
      if (typeof s.voiceoverStart === "number") setVoiceoverStart(s.voiceoverStart);
    }).catch(() => { /* silent */ });
  }, [draftId]);

  const preset = FILTER_PRESETS.find((f) => f.v === activeFilter) || FILTER_PRESETS[0];
  const previewCss = useMemo(() => {
    const parts = [];
    if (preset.css) parts.push(preset.css);
    parts.push(`brightness(${1 + brightness})`);
    parts.push(`contrast(${contrast})`);
    parts.push(`saturate(${saturation})`);
    return parts.join(" ");
  }, [preset, brightness, contrast, saturation]);
  const previewTransform = useMemo(() => {
    const t = [];
    if (rotate) t.push(`rotate(${rotate}deg)`);
    if (flipH) t.push("scaleX(-1)");
    if (flipV) t.push("scaleY(-1)");
    return t.join(" ");
  }, [rotate, flipH, flipV]);
  const aspectRatio = ASPECTS.find((a) => a.v === aspect)?.ratio;

  // ------------- Actions -------------
  const applyTemplate = (t) => {
    setAspect(t.aspect); setFilter(t.filter);
    setTextOverlay(t.overlay); setTextPos(t.pos); setTextAnim(t.anim);
    setTextPosPct(t.pos === "top" ? { x: 50, y: 12 } : t.pos === "middle" ? { x: 50, y: 50 } : { x: 50, y: 90 });
    toast.success(`${t.l} template applied`);
    setActiveTool("text");
    api.post("/studio/analytics/event", { event: "template_applied", payload: { template: t.v } }).catch(() => {});
  };

  const addSticker = (emoji) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Place stickers at scattered positions so they don't stack
    const xPct = 20 + Math.random() * 60;
    const yPct = 30 + Math.random() * 40;
    setStickers((prev) => [...prev, { id, type: "emoji", emoji, xPct, yPct, size: 15, rotate: 0 }]);
  };
  const addTextSticker = (text, shape, color) => {
    if (!text || !text.trim()) { toast.error("Type some text first"); return; }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const xPct = 25 + Math.random() * 50;
    const yPct = 30 + Math.random() * 40;
    setStickers((prev) => [...prev, {
      id, type: "text", text: text.trim().slice(0, 32),
      shape: shape || "chip",
      color: color || "#FF2F6E",
      xPct, yPct, size: 22, rotate: 0,
    }]);
    toast.success("Text sticker added");
  };
  const removeSticker = (id) => setStickers((prev) => prev.filter((s) => s.id !== id));

  // ------------- Multi-text layer helpers -------------
  const commitCurrentText = () => {
    if (!textOverlay.trim()) return false;
    const layer = {
      id: editingCommittedId || `t_${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      content: textOverlay,
      xPct: textPosPct.x, yPct: textPosPct.y,
      scale: textScale, rotate: textRotate,
      anim: textAnim, color: textColor,
      bg: textBg, bgColor: textBgColor,
    };
    setCommittedTexts((arr) => {
      const filtered = arr.filter((t) => t.id !== layer.id);
      return [...filtered, layer];
    });
    return true;
  };
  const clearActiveText = () => {
    setTextOverlay("");
    setTextPos("bottom");
    setTextPosPct({ x: 50, y: 90 });
    setTextScale(1); setTextRotate(0);
    setTextAnim("static"); setTextColor("#FFFFFF");
    setTextBg("none"); setTextBgColor("#000000");
    setEditingCommittedId(null);
  };
  const addNewTextLayer = () => {
    commitCurrentText();
    clearActiveText();
    setTextOverlay("New text");
    setTextPosPct({ x: 50, y: 50 });
    setActiveTool("text");
    toast.success("New text layer");
  };
  const editCommittedText = (id) => {
    const layer = committedTexts.find((t) => t.id === id);
    if (!layer) return;
    commitCurrentText();       // commit whatever's in the active slot
    setTextOverlay(layer.content);
    setTextPosPct({ x: layer.xPct, y: layer.yPct });
    setTextScale(layer.scale || 1);
    setTextRotate(layer.rotate || 0);
    setTextAnim(layer.anim || "static");
    setTextColor(layer.color || "#FFFFFF");
    setTextBg(layer.bg || "none");
    setTextBgColor(layer.bgColor || "#000000");
    setEditingCommittedId(id);
    setCommittedTexts((arr) => arr.filter((t) => t.id !== id));
    setActiveTool("text");
  };

  // ------------- Music helpers -------------
  const loadMusicFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("audio")) { toast.error("Pick an audio file (MP3/WAV/M4A)"); return; }
    if (f.size > 20 * 1024 * 1024) { toast.error("Music track too big (max 20 MB)"); return; }
    if (musicUrl) URL.revokeObjectURL(musicUrl);
    const u = URL.createObjectURL(f);
    setMusicFile(f);
    setMusicUrl(u);
    setMusicName(f.name.replace(/\.[^.]+$/, "").slice(0, 40));
    setMusicStart(0);
    toast.success(`Music loaded: ${f.name.slice(0, 30)}`);
    api.post("/studio/analytics/event", { event: "music_added", payload: {} }).catch(() => {});
  };
  const clearMusic = () => {
    if (musicUrl) URL.revokeObjectURL(musicUrl);
    setMusicFile(null); setMusicUrl(""); setMusicName("");
    setMusicPreviewing(false);
    if (musicAudioRef.current) { try { musicAudioRef.current.pause(); } catch { /* noop */ } }
  };
  const toggleMusicPreview = () => {
    const a = musicAudioRef.current;
    if (!a) return;
    if (musicPreviewing) { a.pause(); setMusicPreviewing(false); }
    else { a.currentTime = musicStart; a.volume = Math.min(1, musicVolume); a.play().then(() => setMusicPreviewing(true)).catch(() => setMusicPreviewing(false)); }
  };
  const fetchMusicLibrary = async () => {
    if (musicLibrary.length > 0 || libraryLoading) return;
    setLibraryLoading(true);
    try {
      const r = await api.get("/studio/music/library");
      setMusicLibrary(r.data?.tracks || []);
    } catch {
      toast.error("Couldn't load music library");
    } finally { setLibraryLoading(false); }
  };
  const pickLibraryTrack = async (track) => {
    setLibraryPickingId(track.id);
    try {
      // Full URL for the WAV asset (REACT_APP_BACKEND_URL prefix)
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${track.url}`;
      const res = await fetch(fullUrl);
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const f = new File([blob], `${track.id}.wav`, { type: "audio/wav" });
      loadMusicFile(f);
      // Override music name to the friendly track title
      setMusicName(`${track.title} · ${track.mood}`);
      setActiveMusicTrackId(track.id);
      api.post("/studio/analytics/event", { event: "music_library_pick", payload: { track: track.id, mood: track.mood } }).catch(() => {});
    } catch {
      toast.error("Couldn't load track");
    } finally { setLibraryPickingId(null); }
  };

  // ------------- Pro Music (Epidemic Sound) -------------
  const searchPro = async () => {
    setProLoading(true);
    try {
      const params = new URLSearchParams();
      if (proSearchTerm.trim()) params.set("term", proSearchTerm.trim());
      if (proSelectedMood) params.set("mood", proSelectedMood);
      params.set("limit", "24");
      const r = await api.get(`/studio/music/pro/search?${params.toString()}`);
      setProTracks(r.data?.tracks || []);
      if (proMoods.length === 0) {
        api.get("/studio/music/pro/moods").then((m) => setProMoods(m.data?.moods || [])).catch(() => {});
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pro Music search failed");
    } finally { setProLoading(false); }
  };
  const toggleProPreview = async (track) => {
    const el = proPreviewRef.current;
    if (!el) return;
    if (proPreviewingId === track.id) {
      el.pause(); setProPreviewingId(null);
      return;
    }
    // Fetch signed download URL (streams the MP3 for preview — same file we'd export with)
    try {
      const r = await api.get(`/studio/music/pro/track/${track.id}/download`);
      el.src = r.data?.url;
      el.currentTime = 0; el.volume = 0.85;
      await el.play();
      setProPreviewingId(track.id);
    } catch {
      toast.error("Preview unavailable");
    }
  };
  const pickProTrack = async (track) => {
    setProPickingId(track.id);
    try {
      const r = await api.get(`/studio/music/pro/track/${track.id}/download`);
      const signedUrl = r.data?.url;
      if (!signedUrl) throw new Error("No download URL");
      const audioRes = await fetch(signedUrl);
      const blob = await audioRes.blob();
      const f = new File([blob], `${track.id}.mp3`, { type: "audio/mpeg" });
      loadMusicFile(f);
      setMusicName(`${track.title} · ${(track.artists || []).join(", ")}`);
      setActiveMusicTrackId(null); // Pro tracks don't have Nexus-side beat grid
      api.post("/studio/analytics/event", { event: "pro_music_pick", payload: { track: track.id, artists: track.artists } }).catch(() => {});
      toast.success(`Added ${track.title}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add Pro track");
    } finally { setProPickingId(null); }
  };

  // ------------- Voiceover helpers -------------
  const loadVoiceoverFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("audio")) { toast.error("Pick an audio file (MP3/WAV/M4A)"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Voiceover too big (max 15 MB)"); return; }
    if (voiceoverUrl) URL.revokeObjectURL(voiceoverUrl);
    const u = URL.createObjectURL(f);
    setVoiceoverFile(f);
    setVoiceoverUrl(u);
    setVoiceoverName(f.name.replace(/\.[^.]+$/, "").slice(0, 40));
    toast.success(`Voiceover loaded: ${f.name.slice(0, 30)}`);
    api.post("/studio/analytics/event", { event: "voiceover_added", payload: {} }).catch(() => {});
  };
  const clearVoiceover = () => {
    if (voiceoverUrl) URL.revokeObjectURL(voiceoverUrl);
    setVoiceoverFile(null); setVoiceoverUrl(""); setVoiceoverName("");
    setVoiceoverPreviewing(false);
    if (voiceoverAudioRef.current) { try { voiceoverAudioRef.current.pause(); } catch { /* noop */ } }
  };
  const toggleVoiceoverPreview = () => {
    const a = voiceoverAudioRef.current;
    if (!a) return;
    if (voiceoverPreviewing) { a.pause(); setVoiceoverPreviewing(false); }
    else { a.currentTime = 0; a.volume = Math.min(1, voiceoverVolume); a.play().then(() => setVoiceoverPreviewing(true)).catch(() => setVoiceoverPreviewing(false)); }
  };

  // ------------- Sound FX helpers -------------
  const fetchFxLibrary = async () => {
    if (fxLibrary.length > 0 || fxLoading) return;
    setFxLoading(true);
    try {
      const r = await api.get("/studio/fx/library");
      setFxLibrary(r.data?.fx || []);
    } catch { /* silent */ } finally { setFxLoading(false); }
  };
  const previewFx = async (fx) => {
    try {
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${fx.url}`;
      const el = fxAudioPreviewRef.current;
      if (!el) return;
      el.src = fullUrl; el.currentTime = 0; el.volume = 0.85;
      el.play().catch(() => {});
    } catch { /* noop */ }
  };
  const addFxSlot = async (fx) => {
    if (fxSlots.length >= 4) { toast.error("Max 4 sound effects per edit"); return; }
    setFxPickingId(fx.id);
    try {
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${fx.url}`;
      const res = await fetch(fullUrl);
      const blob = await res.blob();
      const f = new File([blob], `${fx.id}.wav`, { type: "audio/wav" });
      const url = URL.createObjectURL(f);
      // Place new FX at the middle of the edit for something visible
      const atSec = Math.min(Math.max(0.5, totalEditDuration() / 2), Math.max(0.5, totalEditDuration() - 0.3));
      setFxSlots((prev) => [...prev, { id: `${fx.id}-${Date.now()}`, fxId: fx.id, title: fx.title, file: f, url, atSec, volume: 0.85, durationMs: fx.duration_ms || 500 }]);
      toast.success(`Added ${fx.title}`);
      api.post("/studio/analytics/event", { event: "fx_added", payload: { fx: fx.id } }).catch(() => {});
    } catch {
      toast.error("Couldn't add sound FX");
    } finally { setFxPickingId(null); }
  };
  const removeFxSlot = (id) => {
    setFxSlots((prev) => {
      const s = prev.find((x) => x.id === id);
      if (s?.url) URL.revokeObjectURL(s.url);
      return prev.filter((x) => x.id !== id);
    });
  };
  const updateFxSlot = (id, patch) => {
    setFxSlots((prev) => prev.map((s) => s.id === id ? { ...s, ...patch } : s));
  };

  // ------------- Beat sync -------------
  /** Detect beats in an arbitrary audio File using Web Audio API + energy-peak detection.
   * Fallback for uploaded music tracks that aren't in the Nexus Beats library.
   * Returns array of timestamps (seconds) of detected beats. */
  const detectBeatsInFile = async (audioFile) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) throw new Error("Web Audio API unavailable");
    const ctx = new AudioContextClass();
    try {
      const buf = await ctx.decodeAudioData(await audioFile.arrayBuffer());
      const data = buf.getChannelData(0);
      const sr = buf.sampleRate;
      // 50ms windows for onset detection
      const windowMs = 50;
      const winN = Math.floor(sr * windowMs / 1000);
      const energies = [];
      for (let i = 0; i < data.length; i += winN) {
        let e = 0;
        for (let j = i; j < Math.min(i + winN, data.length); j++) e += Math.abs(data[j]);
        energies.push(e);
      }
      // Peak = window energy > 1.5× local mean, min 200ms between beats
      const beats = [];
      const minGapWindows = Math.floor(200 / windowMs);
      let lastIdx = -100;
      for (let i = 2; i < energies.length - 2; i++) {
        const localMean = (energies[i - 2] + energies[i - 1] + energies[i + 1] + energies[i + 2]) / 4;
        if (energies[i] > localMean * 1.5 && i - lastIdx > minGapWindows) {
          beats.push((i * windowMs) / 1000);
          lastIdx = i;
        }
      }
      return beats;
    } finally {
      try { await ctx.close(); } catch { /* noop */ }
    }
  };

  /** Snap each clip's trimEnd to align with the nearest musical beat.
   * Uses backend beat grid for Nexus Beats tracks; falls back to client-side onset
   * detection for uploaded music. */
  const snapClipsToBeats = async () => {
    if (extraClips.length === 0) { toast.error("Add at least one more clip to beat-sync"); return; }
    if (!musicFile && !activeMusicTrackId) { toast.error("Load a music track first"); return; }
    setBeatSyncing(true);
    try {
      let beats;
      if (activeMusicTrackId) {
        const r = await api.get(`/studio/music/library/${activeMusicTrackId}/beats`);
        beats = (r.data?.beats || []).filter((b) => b > 0.5);
      } else {
        // Uploaded music — detect client-side
        beats = await detectBeatsInFile(musicFile);
        beats = beats.filter((b) => b > 0.3);
      }
      if (!beats || beats.length === 0) throw new Error("No beats detected");
      const totalClips = 1 + extraClips.length;
      const beatsPerClip = Math.max(1, Math.floor(beats.length / totalClips));
      const boundaries = [];
      for (let i = 1; i <= totalClips; i++) {
        const beatIdx = Math.min(beats.length - 1, i * beatsPerClip);
        boundaries.push(beats[beatIdx]);
      }
      let cursor = 0;
      const primaryLen = Math.min(MAX_VIDEO_SEC, boundaries[0] - cursor);
      setTrimStart(0);
      setTrimEnd(Math.max(0.5, primaryLen));
      cursor = boundaries[0];
      const newExtras = extraClips.map((c, i) => {
        const targetEnd = boundaries[i + 1] || (cursor + Math.max(0.5, c.trimEnd - c.trimStart));
        const wantLen = Math.max(0.5, targetEnd - cursor);
        const newTrimEnd = Math.min(c.duration || wantLen, c.trimStart + wantLen);
        cursor = targetEnd;
        return { ...c, trimEnd: newTrimEnd };
      });
      setExtraClips(newExtras);
      setTransitions((prev) => prev.map(() => "fade"));
      toast.success(`Snapped ${totalClips} clips to ${beats.length} beats`);
      api.post("/studio/analytics/event", { event: "beat_sync_applied", payload: { track: activeMusicTrackId || "uploaded", clips: totalClips, beats: beats.length } }).catch(() => {});
    } catch {
      toast.error("Beat sync failed — try a track with a clearer beat");
    } finally { setBeatSyncing(false); }
  };

  // ------------- Freemium checkout -------------
  const startCreatorProCheckout = async () => {
    setCheckoutBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: "creator_pro_monthly",
        origin_url: window.location.origin,
      });
      if (r.data?.checkout_url) {
        window.location.href = r.data.checkout_url;
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upgrade not available yet — Creator Pro launches soon");
      setCheckoutBusy(false);
    }
  };

  /** Render the Nexus watermark as a transparent PNG for burn-in. */
  const renderWatermarkPng = (sizePx) => new Promise((resolve, reject) => {
    const canvas = document.createElement("canvas");
    const w = Math.max(200, Math.floor(sizePx));
    const h = Math.floor(w * 0.32);
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Rounded pill background
    const r = h / 2;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.quadraticCurveTo(w, 0, w, r);
    ctx.lineTo(w, h - r);
    ctx.quadraticCurveTo(w, h, w - r, h);
    ctx.lineTo(r, h);
    ctx.quadraticCurveTo(0, h, 0, h - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fillStyle = "rgba(5,5,7,0.55)";
    ctx.fill();
    // Nexus gradient dot
    const grad = ctx.createLinearGradient(0, 0, w, h);
    grad.addColorStop(0, "#FF2F6E");
    grad.addColorStop(0.5, "#8B5CF6");
    grad.addColorStop(1, "#3B82F6");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(h * 0.55, h / 2, h * 0.28, 0, Math.PI * 2);
    ctx.fill();
    // Text
    ctx.fillStyle = "#FFFFFF";
    ctx.textBaseline = "middle";
    ctx.font = `bold ${Math.floor(h * 0.45)}px "Inter", "Helvetica", Arial, sans-serif`;
    ctx.fillText("NEXUS", h * 1.05, h / 2 + h * 0.03);
    ctx.font = `${Math.floor(h * 0.24)}px "Inter", "Helvetica", Arial, sans-serif`;
    ctx.fillStyle = "rgba(255,255,255,0.65)";
    ctx.fillText("nexus.co.uk", h * 1.05 + Math.floor(h * 0.45) * 3.2, h / 2 + h * 0.03);
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error("watermark render failed")), "image/png");
  });

  const generateCaption = async () => {
    setCaptionBusy(true);
    try {
      const r = await api.post("/studio/caption", {
        context: captionContext || (kind === "video" ? "a 15-second creator clip" : "a creator image post"),
        vibe: "playful",
        platforms: ["instagram", "tiktok"],
      });
      setCaption(r.data);
      toast.success("Caption ready");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Caption failed");
    } finally { setCaptionBusy(false); }
  };
  const generateSubs = async () => {
    if (kind !== "video") return;
    setSubsBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const r = await api.post("/studio/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      setSubs(r.data);
      toast.success("Subtitles ready — burned in on export");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Transcription failed");
    } finally { setSubsBusy(false); }
  };
  const copyCaption = async () => {
    if (!caption) return;
    const text = caption.caption + "\n\n" + (caption.hashtags || []).join(" ");
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  /** Grab a JPEG keyframe from a video File at the given time offset (seconds). */
  const extractVideoFrame = (videoFile, atSec = 0) => new Promise((resolve, reject) => {
    const el = document.createElement("video");
    el.preload = "auto";
    el.muted = true;
    el.playsInline = true;
    el.src = URL.createObjectURL(videoFile);
    const cleanup = () => { try { URL.revokeObjectURL(el.src); } catch { /* noop */ } };
    el.onloadedmetadata = () => {
      const t = Math.max(0, Math.min(atSec, (el.duration || 1) - 0.1));
      el.currentTime = isFinite(t) ? t : 0;
    };
    el.onseeked = () => {
      try {
        const canvas = document.createElement("canvas");
        const w = el.videoWidth || 640;
        const h = el.videoHeight || 360;
        // Cap frame size to keep the upload small — model doesn't need 4K
        const maxDim = 800;
        const scale = Math.min(1, maxDim / Math.max(w, h));
        canvas.width = Math.max(2, Math.floor(w * scale));
        canvas.height = Math.max(2, Math.floor(h * scale));
        canvas.getContext("2d").drawImage(el, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => {
          cleanup();
          if (!b) return reject(new Error("frame extract failed"));
          resolve(new File([b], `frame.jpg`, { type: "image/jpeg" }));
        }, "image/jpeg", 0.85);
      } catch (e) { cleanup(); reject(e); }
    };
    el.onerror = () => { cleanup(); reject(new Error("video load failed")); };
  });

  /** Downscale an image File to a smaller JPEG for AI upload. */
  const downscaleImage = (imgFile) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(imgFile);
    const img = new Image();
    img.onload = () => {
      const maxDim = 800;
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(2, Math.floor(img.naturalWidth * scale));
      canvas.height = Math.max(2, Math.floor(img.naturalHeight * scale));
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((b) => {
        URL.revokeObjectURL(url);
        if (!b) return reject(new Error("image resize failed"));
        resolve(new File([b], "frame.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });

  /** Apply an AI suggestion to a specific clip id (or "primary"). Filter is per-clip;
   * everything else (aspect, text) is global and only set for the primary clip. */
  const applySuggestionToClip = (clipId, s) => {
    if (!s) return;
    if (clipId === "primary") {
      if (s.filter) setFilter(s.filter);
      if (s.aspect) setAspect(s.aspect);
      if (typeof s.textOverlay === "string" && s.textOverlay) setTextOverlay(s.textOverlay);
      if (s.textPos) setTextPos(s.textPos);
      if (s.textPosPct && typeof s.textPosPct.x === "number" && typeof s.textPosPct.y === "number") {
        setTextPosPct({ x: s.textPosPct.x, y: s.textPosPct.y });
      } else if (s.textPos) {
        // Legacy drafts — derive pct coords from the preset
        setTextPosPct(s.textPos === "top" ? { x: 50, y: 12 } : s.textPos === "middle" ? { x: 50, y: 50 } : { x: 50, y: 90 });
      }
      if (typeof s.textScale === "number") setTextScale(s.textScale);
      if (typeof s.textRotate === "number") setTextRotate(s.textRotate);
      if (s.textBg) setTextBg(s.textBg);
      if (s.textBgColor) setTextBgColor(s.textBgColor);
      if (Array.isArray(s.committedTexts)) setCommittedTexts(s.committedTexts);
      if (s.textColor) setTextColor(s.textColor);
      if (s.textAnim) setTextAnim(s.textAnim);
    } else {
      setExtraClips((prev) => prev.map((c) => c.id === clipId ? { ...c, filter: s.filter || c.filter || "none" } : c));
    }
  };

  const runSmartSuggest = async ({ perClip = false, refine = false } = {}) => {
    if (suggestBusy) return;
    const refinePromptClean = refine ? (refinePrompt || "").trim() : "";
    if (refine && !refinePromptClean) { toast.error("Type what you want to change"); return; }
    setSuggestBusy(true);
    setSuggestResult(null);
    try {
      const clips = perClip && extraClips.length
        ? [{ id: "primary", file, trimStart, trimEnd, filter }, ...extraClips.map((c) => ({ id: c.id, file: c.file, trimStart: c.trimStart, trimEnd: c.trimEnd, filter: c.filter || "none" }))]
        : [{ id: activeClipId, file: (isActivePrimary ? file : (activeExtra?.file || file)), trimStart: activeTrimStart, trimEnd: activeTrimEnd, filter: activeFilter }];

      const perClipResults = [];
      for (const clip of clips) {
        let frame;
        if (kind === "video" || clip.file?.type?.startsWith("video")) {
          const mid = ((clip.trimStart || 0) + (clip.trimEnd || 0)) / 2 || 0.5;
          frame = await extractVideoFrame(clip.file, mid);
        } else {
          frame = await downscaleImage(clip.file);
        }
        const fd = new FormData();
        fd.append("file", frame, "frame.jpg");
        if (captionContext) fd.append("context", captionContext.slice(0, 400));
        if (refinePromptClean) fd.append("refine_prompt", refinePromptClean);
        if (clip.filter) fd.append("current_filter", clip.filter);
        const r = await api.post("/studio/smart-suggest", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        applySuggestionToClip(clip.id, r.data);
        perClipResults.push({ id: clip.id, ...r.data });
      }

      const first = perClipResults[0];
      setSuggestResult({
        vibe: first?.vibe || "AI look applied",
        count: perClipResults.length,
        appliedTo: perClip ? "each clip" : (isActivePrimary ? "Main clip" : `Clip ${extraClips.findIndex((c) => c.id === activeClipId) + 2}`),
        results: perClipResults,
        refined: !!refinePromptClean,
      });
      toast.success(refine
        ? `Refined: ${refinePromptClean.slice(0, 40)}`
        : (perClip ? `AI look applied to ${perClipResults.length} clip${perClipResults.length > 1 ? "s" : ""}` : "AI look applied"));
      if (refine) setRefinePrompt("");
      api.post("/studio/analytics/event", { event: "smart_suggest_applied", payload: { count: perClipResults.length, per_clip: perClip, refined: !!refinePromptClean } }).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Smart suggest failed");
    } finally {
      setSuggestBusy(false);
    }
  };

  /** AI Auto-Editor — Claude reads keyframes from every clip → applies full edit. */
  const runAutoEdit = async () => {
    if (autoEditBusy) return;
    if (kind !== "video") { toast.error("Auto-Edit works on video edits"); return; }
    setAutoEditBusy(true);
    setAutoEditResult(null);
    try {
      const fd = new FormData();
      // Primary keyframe
      const primaryMid = ((trimStart || 0) + (trimEnd || 0)) / 2 || 0.5;
      const primaryFrame = await extractVideoFrame(file, primaryMid);
      fd.append("clip0", primaryFrame, "clip0.jpg");
      // Extra clips (up to 2 more for the 3-clip cap)
      for (let i = 0; i < Math.min(extraClips.length, 2); i++) {
        const c = extraClips[i];
        const mid = ((c.trimStart || 0) + (c.trimEnd || 0)) / 2 || 0.5;
        const f = await extractVideoFrame(c.file, mid);
        fd.append(`clip${i + 1}`, f, `clip${i + 1}.jpg`);
      }
      if (captionContext) fd.append("context", captionContext.slice(0, 400));
      if (musicName) fd.append("current_music_mood", musicName.split(" · ")[1] || "");

      const r = await api.post("/studio/auto-edit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      const plan = r.data;

      // ---- Apply the AI edit plan ----
      // 1. Per-clip filters
      if (plan.per_clip?.[0]?.filter) setFilter(plan.per_clip[0].filter);
      for (let i = 0; i < Math.min(extraClips.length, plan.per_clip.length - 1); i++) {
        const pcFilter = plan.per_clip[i + 1]?.filter;
        if (pcFilter) {
          setExtraClips((prev) => prev.map((c, idx) => idx === i ? { ...c, filter: pcFilter } : c));
        }
      }
      // 2. Transitions
      if (Array.isArray(plan.transitions) && plan.transitions.length > 0) {
        setTransitions(plan.transitions.slice(0, extraClips.length));
      }
      // 3. Text overlay
      if (plan.text_overlay) {
        setTextOverlay(plan.text_overlay);
        if (plan.text_pos) setTextPos(plan.text_pos);
        if (plan.text_anim) setTextAnim(plan.text_anim);
        if (plan.text_color) setTextColor(plan.text_color);
      }
      // 4. Caption + hashtags cached
      if (plan.caption || plan.hashtags) {
        setCaption({ caption: plan.caption || "", hashtags: plan.hashtags || [], hooks: [], source: "auto-edit" });
      }
      // 5. Music mood → auto-pick matching Nexus Beats track if none loaded yet
      if (plan.music_mood && !musicFile) {
        try {
          if (musicLibrary.length === 0) await fetchMusicLibrary();
          const pool = (musicLibrary.length > 0 ? musicLibrary : []).filter((t) => t.mood === plan.music_mood);
          if (pool.length > 0) {
            const pick = pool[Math.floor(Math.random() * pool.length)];
            await pickLibraryTrack(pick);
          }
        } catch { /* silent */ }
      }
      // 6. If multi-clip + music loaded → auto beat-sync
      setTimeout(() => {
        if (extraClips.length > 0 && (musicFile || activeMusicTrackId)) {
          snapClipsToBeats().catch(() => {});
        }
      }, 500);

      setAutoEditResult({ vibe: plan.vibe || "AI edit applied", music: plan.music_mood, source: plan.source });
      toast.success("Auto-Edit applied — check the timeline");
      api.post("/studio/analytics/event", { event: "auto_edit_applied", payload: { clips: 1 + Math.min(extraClips.length, 2), source: plan.source } }).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Auto-Edit failed");
    } finally {
      setAutoEditBusy(false);
    }
  };

  const saveDraft = async ({ silent = false } = {}) => {
    if (!silent) setAutoSaving(false);
    try {
      let source_url = savedSourceUrl;
      if (!source_url && draftId) {
        const existing = await api.get(`/studio/drafts/${draftId}`);
        source_url = existing.data?.source_url || null;
      }
      if (!source_url || source_url.startsWith("local://")) {
        const up = await uploadFile(file, { label: "source video" });
        source_url = up.url;
        setSavedSourceUrl(source_url);
      }
      const r = await api.post("/studio/drafts", {
        id: draftId || undefined, kind, source_url, title: file.name,
        hire_id: hireId || undefined,
        edit_state: {
          aspect, filter, brightness, contrast, saturation, speed, muted,
          trimStart, trimEnd, textOverlay, textPos, textPosPct, textScale, textRotate, textAnim, textColor, textBg, textBgColor, committedTexts, stickers,
          rotate, flipH, flipV, reverse, volume, fadeIn, fadeOut, splits,
          chromaEnabled, chromaColor, chromaSimilarity, chromaBlend, chromaBgColor,
          musicName, musicVolume, musicStart, musicFadeIn, musicFadeOut, musicDuck,
          voiceoverName, voiceoverVolume, voiceoverStart,
          subs: subs ? { text: subs.text, srt: subs.srt } : null, caption: caption || null,
        },
      });
      setLastSavedAt(new Date());
      if (!silent) toast.success("Draft saved");
      onDraftSaved?.(r.data.id);
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setAutoSaving(false);
    }
  };

  // Auto-save every 20s while edit state changes — silent, doesn't toast.
  useEffect(() => {
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaving(true);
    autoSaveTimerRef.current = setTimeout(() => {
      saveDraft({ silent: true }).catch(() => {});
    }, 20000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [aspect, filter, brightness, contrast, saturation, speed, muted, trimStart, trimEnd,
      textOverlay, textPos, textPosPct, textScale, textRotate, textAnim, textColor, textBg, textBgColor, committedTexts, stickers, rotate, flipH, flipV, reverse,
      volume, fadeIn, fadeOut, splits, musicName, musicVolume, musicStart, musicFadeIn,
      musicFadeOut, musicDuck, voiceoverName, voiceoverVolume, voiceoverStart,
      chromaEnabled, chromaColor, chromaSimilarity, chromaBlend, chromaBgColor,
      extraClips, transitions]);

  // ------------- Export helpers -------------
  const buildVideoFilter = (clipFilter = null) => {
    const eqPreset = clipFilter != null
      ? (FILTER_PRESETS.find((f) => f.v === clipFilter) || FILTER_PRESETS[0])
      : preset;
    const parts = [];
    if (aspectRatio && naturalW && naturalH) {
      const sourceRatio = naturalW / naturalH;
      let cw = naturalW, ch = naturalH;
      if (aspectRatio > sourceRatio) ch = Math.floor(naturalW / aspectRatio);
      else cw = Math.floor(naturalH * aspectRatio);
      cw = Math.max(2, cw - (cw % 2)); ch = Math.max(2, ch - (ch % 2));
      parts.push(`crop=${cw}:${ch}:(iw-${cw})/2:(ih-${ch})/2`);
    }
    // Rotation
    if (rotate === 90) parts.push("transpose=1");
    else if (rotate === 180) parts.push("transpose=1,transpose=1");
    else if (rotate === 270) parts.push("transpose=2");
    // Flip
    if (flipH) parts.push("hflip");
    if (flipV) parts.push("vflip");
    if (eqPreset.eq) parts.push(eqPreset.eq);
    parts.push(`eq=brightness=${brightness}:contrast=${contrast}:saturation=${saturation}`);
    // Green-screen / chroma-key: the keyed colour goes transparent in the alpha channel.
    // On export we then flatten onto a solid background colour via the yuv420p pipeline.
    if (chromaEnabled) {
      const keyHex = (chromaColor || "#00FF00").replace("#", "").toLowerCase();
      const sim = Math.max(0.01, Math.min(0.5, Number(chromaSimilarity) || 0.3)).toFixed(3);
      const blend = Math.max(0, Math.min(0.3, Number(chromaBlend) || 0.1)).toFixed(3);
      parts.push(`chromakey=0x${keyHex}:${sim}:${blend}`);
    }
    // Reverse
    if (reverse) parts.push("reverse");
    if (speed !== 1) parts.push(`setpts=${(1 / speed).toFixed(4)}*PTS`);
    // Fades
    const dur = Math.max(0.5, Math.min(MAX_VIDEO_SEC, trimEnd - trimStart));
    if (fadeIn > 0) parts.push(`fade=t=in:st=0:d=${Math.min(fadeIn, dur / 2).toFixed(2)}`);
    if (fadeOut > 0) parts.push(`fade=t=out:st=${Math.max(0, dur - fadeOut).toFixed(2)}:d=${Math.min(fadeOut, dur / 2).toFixed(2)}`);
    // Text layers — active overlay + previously committed
    const drawtextFor = (content, xPctRaw, yPctRaw, scaleRaw, colorHex, animName, bgKind, bgCol, startT, endT) => {
      if (!content || !content.trim()) return;
      const safe = content.replace(/'/g, "\\'").replace(/:/g, "\\:");
      const xPct = Math.max(2, Math.min(98, xPctRaw ?? 50)) / 100;
      const yPct = Math.max(4, Math.min(96, yPctRaw ?? 90)) / 100;
      const x = `(w*${xPct.toFixed(3)})-(text_w/2)`;
      const y = `(h*${yPct.toFixed(3)})-(text_h/2)`;
      const hex = (colorHex || "#FFFFFF").replace("#", "");
      const scale = Math.max(0.4, Math.min(3, scaleRaw || 1));
      const fontExpr = `(h/${(16 / scale).toFixed(3)})`;
      const alphaExpr = animName && animName !== "static"
        ? `:alpha='if(lt(t\\,0.6)\\,t/0.6\\,1)'`
        : "";
      // Background box — ffmpeg drawtext natively supports a single-colour box behind the text.
      // Marker is approximated as a semi-transparent solid box in video export.
      let boxExpr = "";
      if (bgKind && bgKind !== "none") {
        const bgHex = (bgCol || "#000000").replace("#", "");
        const alpha = bgKind === "semi" ? 0.6 : bgKind === "marker" ? 0.85 : 1;
        boxExpr = `:box=1:boxcolor=0x${bgHex}@${alpha}:boxborderw=12`;
      }
      const borderExpr = (!bgKind || bgKind === "none") ? ":borderw=3:bordercolor=black@0.65" : "";
      // Time-gate captions so each Whisper segment only shows during its own window.
      const enableExpr = (typeof startT === "number" && typeof endT === "number" && endT > startT)
        ? `:enable='between(t\\,${startT.toFixed(3)}\\,${endT.toFixed(3)})'`
        : "";
      parts.push(
        `drawtext=text='${safe}':fontcolor=0x${hex}:fontsize=${fontExpr}${borderExpr}${boxExpr}:x=${x}:y=${y}${alphaExpr}${enableExpr}`
      );
    };
    for (const ct of committedTexts) {
      drawtextFor(ct.content, ct.xPct, ct.yPct, ct.scale, ct.color, ct.anim, ct.bg, ct.bgColor, ct.startTime, ct.endTime);
    }
    if (textOverlay && textOverlay.trim()) {
      drawtextFor(textOverlay, textPosPct?.x, textPosPct?.y, textScale, textColor, textAnim, textBg, textBgColor);
    }
    return parts.join(",");
  };
  const buildAudioArgs = () => {
    if (muted || volume === 0) return ["-an"];
    const filters = [];
    if (speed !== 1) filters.push(`atempo=${Math.min(2, Math.max(0.5, speed))}`);
    if (reverse) filters.push("areverse");
    if (volume !== 1) filters.push(`volume=${Math.max(0, Math.min(3, volume)).toFixed(2)}`);
    const dur = Math.max(0.5, Math.min(MAX_VIDEO_SEC, trimEnd - trimStart));
    if (fadeIn > 0) filters.push(`afade=t=in:st=0:d=${Math.min(fadeIn, dur / 2).toFixed(2)}`);
    if (fadeOut > 0) filters.push(`afade=t=out:st=${Math.max(0, dur - fadeOut).toFixed(2)}:d=${Math.min(fadeOut, dur / 2).toFixed(2)}`);
    return filters.length ? ["-af", filters.join(",")] : [];
  };

  // Compute the final exported video dimensions so we can position sticker overlays.
  const computeExportDims = () => {
    if (extraClips.length > 0) return { W: 1080, H: 1920 }; // multi-clip always normalises here
    if (!aspectRatio) return { W: naturalW || 720, H: naturalH || 1280 };
    const sourceRatio = (naturalW || 720) / (naturalH || 1280);
    let cw = naturalW || 720, ch = naturalH || 1280;
    if (aspectRatio > sourceRatio) ch = Math.floor(cw / aspectRatio);
    else cw = Math.floor(ch * aspectRatio);
    cw = Math.max(2, cw - (cw % 2)); ch = Math.max(2, ch - (ch % 2));
    return { W: cw, H: ch };
  };

  /** Render an emoji OR text-badge sticker to a transparent PNG at the given pixel size. */
  const renderStickerPng = (s, sizePx) => new Promise((resolve, reject) => {
    if (s && s.type === "text") {
      const canvas = document.createElement("canvas");
      const h = Math.max(64, Math.floor(sizePx));
      const ctx0 = canvas.getContext("2d");
      const fontSize = Math.floor(h * 0.55);
      ctx0.font = `900 ${fontSize}px "Inter","Helvetica",Arial,sans-serif`;
      const textW = ctx0.measureText(s.text).width;
      const shape = s.shape || "chip";
      const padX = shape === "tag" ? h * 0.6 : h * 0.55;
      const w = Math.min(1600, Math.floor(textW + padX * 2));
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.textBaseline = "middle";
      ctx.font = `900 ${fontSize}px "Inter","Helvetica",Arial,sans-serif`;
      const color = s.color || "#FF2F6E";
      if (shape === "chip") {
        const r = h / 2;
        ctx.beginPath(); ctx.moveTo(r, 0); ctx.lineTo(w - r, 0);
        ctx.quadraticCurveTo(w, 0, w, r); ctx.lineTo(w, h - r);
        ctx.quadraticCurveTo(w, h, w - r, h); ctx.lineTo(r, h);
        ctx.quadraticCurveTo(0, h, 0, h - r); ctx.lineTo(0, r);
        ctx.quadraticCurveTo(0, 0, r, 0); ctx.closePath();
        ctx.fillStyle = color; ctx.fill();
        ctx.fillStyle = "#FFFFFF"; ctx.textAlign = "center";
        ctx.fillText(s.text, w / 2, h / 2);
      } else if (shape === "banner") {
        ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(w, 0);
        ctx.lineTo(w - h * 0.4, h); ctx.lineTo(h * 0.4, h); ctx.closePath();
        const grad = ctx.createLinearGradient(0, 0, w, h);
        grad.addColorStop(0, color); grad.addColorStop(1, "#3B82F6");
        ctx.fillStyle = grad; ctx.fill();
        ctx.fillStyle = "#FFFFFF"; ctx.textAlign = "center";
        ctx.fillText(s.text, w / 2, h / 2);
      } else {
        // Price tag
        ctx.beginPath(); ctx.moveTo(h * 0.5, 0); ctx.lineTo(w, 0);
        ctx.lineTo(w, h); ctx.lineTo(h * 0.5, h);
        ctx.lineTo(0, h / 2); ctx.closePath();
        ctx.fillStyle = color; ctx.fill();
        ctx.beginPath(); ctx.arc(h * 0.55, h / 2, h * 0.08, 0, Math.PI * 2);
        ctx.fillStyle = "#0A0A10"; ctx.fill();
        ctx.fillStyle = "#FFFFFF"; ctx.textAlign = "center";
        ctx.fillText(s.text, (w + h * 0.5) / 2, h / 2);
      }
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error("text sticker render failed")), "image/png");
      return;
    }
    // Emoji sticker
    const emoji = typeof s === "string" ? s : s?.emoji;
    const canvas = document.createElement("canvas");
    const sz = Math.max(64, Math.floor(sizePx));
    canvas.width = sz; canvas.height = sz;
    const ctx = canvas.getContext("2d");
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${Math.floor(sz * 0.85)}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",Arial`;
    ctx.fillText(emoji, sz / 2, sz / 2 + sz * 0.02);
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error("sticker render failed")), "image/png");
  });

  const exportVideo = async () => {
    // Freemium gating — reserve a slot and block if over the free cap
    let q = quota;
    try {
      const r = await api.post("/studio/exports/register");
      q = r.data;
      setQuota(q);
    } catch (e) {
      if (e.response?.status === 402) {
        setShowUpgradeModal(true);
        return;
      }
    }
    setExporting(true); setProgress(0);
    try {
      const ffmpeg = await getFFmpeg((p) => setProgress(p));
      const rawOutputName = "raw.mp4";
      const outputName = "out.mp4";
      const subsName = "subs.srt";

      // Build the full clip sequence: primary + extras
      const sequence = [
        { file, trimStart, trimEnd, muted, volume, filter },
        ...extraClips.map((c) => ({ file: c.file, trimStart: c.trimStart, trimEnd: c.trimEnd, muted: c.muted, volume: typeof c.volume === "number" ? c.volume : 1, filter: c.filter || "none" })),
      ];

      if (subs?.srt) await ffmpeg.writeFile(subsName, new TextEncoder().encode(subs.srt));

      // Single-clip fast path
      if (sequence.length === 1) {
        const inputName = "in." + (file.name.split(".").pop() || "mp4").toLowerCase();
        await ffmpeg.writeFile(inputName, await fetchFile(file));
        let vf = buildVideoFilter();
        if (subs?.srt) vf = (vf ? vf + "," : "") + `subtitles=${subsName}:force_style='FontName=Sans,FontSize=18,BorderStyle=3,Outline=1'`;
        const trimArgs = [];
        const start = Math.max(0, Number(trimStart) || 0);
        const end = Math.min(duration || MAX_VIDEO_SEC, Number(trimEnd) || MAX_VIDEO_SEC);
        const dur = Math.min(MAX_VIDEO_SEC, Math.max(0.5, end - start));
        if (start > 0) trimArgs.push("-ss", String(start));
        trimArgs.push("-t", String(dur));
        const args = [
          ...trimArgs, "-i", inputName, "-vf", vf, ...buildAudioArgs(),
          "-c:v", "libx264", "-preset", "ultrafast", "-pix_fmt", "yuv420p",
          "-movflags", "+faststart", rawOutputName,
        ];
        await ffmpeg.exec(args);
      } else {
        // Multi-clip concat path: normalise each clip, then either concat (all "none")
        // or blend via xfade for real transitions.
        const intermediates = [];
        const durations = [];
        for (let i = 0; i < sequence.length; i++) {
          const c = sequence[i];
          const ext = (c.file.name.split(".").pop() || "mp4").toLowerCase();
          const inName = `clip${i}.${ext}`;
          const outName = `norm${i}.mp4`;
          await ffmpeg.writeFile(inName, await fetchFile(c.file));
          const t = Math.max(0.5, c.trimEnd - c.trimStart);
          durations.push(t);
          const args = [
            "-ss", String(c.trimStart), "-t", String(t), "-i", inName,
            "-vf", `${buildVideoFilter(c.filter)},scale=1080:1920:force_original_aspect_ratio=decrease,pad=1080:1920:(ow-iw)/2:(oh-ih)/2,setsar=1`,
            ...(c.muted
              ? ["-an"]
              : (c.volume !== 1 ? ["-af", `volume=${Math.max(0, Math.min(3, c.volume)).toFixed(2)}`] : [])),
            "-c:v", "libx264", "-preset", "ultrafast", "-pix_fmt", "yuv420p",
            "-r", "30",
            outName,
          ];
          await ffmpeg.exec(args);
          intermediates.push(outName);
        }

        const hasRealTransitions = transitions.some((tr) => tr && tr !== "none");
        if (!hasRealTransitions) {
          const listName = "list.txt";
          const listContent = intermediates.map((n) => `file '${n}'`).join("\n");
          await ffmpeg.writeFile(listName, new TextEncoder().encode(listContent));
          await ffmpeg.exec([
            "-f", "concat", "-safe", "0", "-i", listName,
            "-c", "copy", rawOutputName,
          ]);
        } else {
          const args = [];
          intermediates.forEach((n) => { args.push("-i", n); });
          const filterParts = [];
          const xfadeMap = {
            fade: "fade", dissolve: "dissolve", wipe: "wiperight",
            slideleft: "slideleft", slideright: "slideright",
            circleopen: "circleopen", radial: "radial", fadeblack: "fadeblack",
          };
          let currentLabel = "0:v";
          let currentDuration = durations[0];
          for (let i = 1; i < intermediates.length; i++) {
            const tr = transitions[i - 1] || "none";
            const D = 0.5;
            if (tr === "none") {
              const outLbl = `c${i}`;
              filterParts.push(`[${currentLabel}][${i}:v]concat=n=2:v=1:a=0[${outLbl}]`);
              currentLabel = outLbl;
              currentDuration += durations[i];
            } else {
              const outLbl = `x${i}`;
              const offset = Math.max(0, currentDuration - D).toFixed(3);
              const xtype = xfadeMap[tr] || "fade";
              filterParts.push(`[${currentLabel}][${i}:v]xfade=transition=${xtype}:duration=${D}:offset=${offset}[${outLbl}]`);
              currentLabel = outLbl;
              currentDuration = currentDuration + durations[i] - D;
            }
          }
          args.push(
            "-filter_complex", filterParts.join(";"),
            "-map", `[${currentLabel}]`,
            "-c:v", "libx264", "-preset", "ultrafast", "-pix_fmt", "yuv420p",
            "-movflags", "+faststart",
            rawOutputName,
          );
          await ffmpeg.exec(args);
        }
      }

      // ---------- Post-process pass: sticker burn-in + music + voiceover + fx + watermark ----------
      const hasStickers = stickers.length > 0;
      const hasMusic = musicFile && musicUrl;
      const hasVoiceover = voiceoverFile && voiceoverUrl;
      const hasFx = fxSlots.length > 0;
      const needsWatermark = !!q?.watermark_required;
      let finalName = rawOutputName;
      if (hasStickers || hasMusic || hasVoiceover || hasFx || needsWatermark) {
        const { W: outW, H: outH } = computeExportDims();
        const totalDur = totalEditDuration();

        const args = ["-i", rawOutputName];
        // Sticker PNGs — one input each
        const stickerInfo = [];
        if (hasStickers) {
          for (let i = 0; i < stickers.length; i++) {
            const s = stickers[i];
            const sizePx = Math.max(64, Math.floor(Math.min(outW, outH) * (s.size / 100)));
            const blob = await renderStickerPng(s, sizePx);
            const name = `sticker${i}.png`;
            await ffmpeg.writeFile(name, new Uint8Array(await blob.arrayBuffer()));
            args.push("-i", name);
            stickerInfo.push({ idx: i + 1, sizePx, xPct: s.xPct, yPct: s.yPct });
          }
        }
        // Watermark PNG (free users only)
        let watermarkIdx = -1;
        if (needsWatermark) {
          const wmSize = Math.floor(outW * 0.28); // ~28% of width
          const wmBlob = await renderWatermarkPng(wmSize);
          const wmName = `watermark.png`;
          await ffmpeg.writeFile(wmName, new Uint8Array(await wmBlob.arrayBuffer()));
          args.push("-i", wmName);
          watermarkIdx = 1 + stickerInfo.length;
        }
        let musicIdx = -1;
        if (hasMusic) {
          const ext = (musicFile.name.split(".").pop() || "mp3").toLowerCase();
          const mn = `music.${ext}`;
          await ffmpeg.writeFile(mn, await fetchFile(musicFile));
          args.push("-i", mn);
          musicIdx = 1 + stickerInfo.length + (needsWatermark ? 1 : 0);
        }
        let voiceIdx = -1;
        if (hasVoiceover) {
          const ext = (voiceoverFile.name.split(".").pop() || "mp3").toLowerCase();
          const vn = `voice.${ext}`;
          await ffmpeg.writeFile(vn, await fetchFile(voiceoverFile));
          args.push("-i", vn);
          voiceIdx = 1 + stickerInfo.length + (needsWatermark ? 1 : 0) + (hasMusic ? 1 : 0);
        }
        // Sound FX slots — each has its own input + delay
        const fxIndices = [];
        if (hasFx) {
          let base = 1 + stickerInfo.length + (needsWatermark ? 1 : 0) + (hasMusic ? 1 : 0) + (hasVoiceover ? 1 : 0);
          for (let i = 0; i < fxSlots.length; i++) {
            const s = fxSlots[i];
            const fn = `fx${i}.wav`;
            await ffmpeg.writeFile(fn, await fetchFile(s.file));
            args.push("-i", fn);
            fxIndices.push({ idx: base + i, atSec: s.atSec, volume: s.volume });
          }
        }

        // Build filter_complex for video overlays
        const filterParts = [];
        let currentV = "0:v";
        for (let i = 0; i < stickerInfo.length; i++) {
          const s = stickerInfo[i];
          const outLbl = `sv${i}`;
          const x = Math.floor(outW * (s.xPct / 100)) + `-overlay_w/2`;
          const y = Math.floor(outH * (s.yPct / 100)) + `-overlay_h/2`;
          filterParts.push(`[${currentV}][${s.idx}:v]overlay=${x}:${y}:format=auto[${outLbl}]`);
          currentV = outLbl;
        }
        // Watermark overlay — bottom-right with padding, ~60% opacity via format=auto (RGBA)
        if (needsWatermark) {
          const outLbl = `vwm`;
          const padX = Math.floor(outW * 0.03);
          const padY = Math.floor(outH * 0.03);
          filterParts.push(`[${currentV}][${watermarkIdx}:v]overlay=W-w-${padX}:H-h-${padY}:format=auto[${outLbl}]`);
          currentV = outLbl;
        }

        // Audio graph — up to N inputs: [0:a] clip, music, voiceover, each fx slot
        const durClamped = Math.max(0.5, Math.min(MAX_VIDEO_SEC, totalDur || MAX_VIDEO_SEC));
        let audioMap = null;
        const audioMixLabels = [];
        const hasAnyAudio = hasMusic || hasVoiceover || hasFx;
        // Original clip audio
        if (hasAnyAudio) {
          const duckVol = (hasMusic && musicDuck) ? "0.3" : "1.0";
          filterParts.push(`[0:a]volume=${duckVol}[a0]`);
          audioMixLabels.push("[a0]");
        }
        if (hasMusic) {
          const mv = Math.max(0, Math.min(3, musicVolume)).toFixed(2);
          const st = Math.max(0, Number(musicStart) || 0).toFixed(2);
          const fi = Math.min(musicFadeIn, durClamped / 2).toFixed(2);
          const fo = Math.min(musicFadeOut, durClamped / 2).toFixed(2);
          const foStart = Math.max(0, durClamped - Number(fo)).toFixed(2);
          filterParts.push(
            `[${musicIdx}:a]atrim=start=${st},asetpts=PTS-STARTPTS,volume=${mv},afade=t=in:st=0:d=${fi},afade=t=out:st=${foStart}:d=${fo}[music]`
          );
          audioMixLabels.push("[music]");
        }
        if (hasVoiceover) {
          const vv = Math.max(0, Math.min(3, voiceoverVolume)).toFixed(2);
          const delayMs = Math.max(0, Math.floor((Number(voiceoverStart) || 0) * 1000));
          filterParts.push(
            `[${voiceIdx}:a]volume=${vv},adelay=${delayMs}|${delayMs}[voice]`
          );
          audioMixLabels.push("[voice]");
        }
        for (let i = 0; i < fxIndices.length; i++) {
          const s = fxIndices[i];
          const vv = Math.max(0, Math.min(2, s.volume)).toFixed(2);
          const delayMs = Math.max(0, Math.floor(s.atSec * 1000));
          filterParts.push(`[${s.idx}:a]volume=${vv},adelay=${delayMs}|${delayMs}[fx${i}]`);
          audioMixLabels.push(`[fx${i}]`);
        }
        if (audioMixLabels.length >= 2) {
          filterParts.push(`${audioMixLabels.join("")}amix=inputs=${audioMixLabels.length}:duration=first:normalize=0[aout]`);
          audioMap = "[aout]";
        } else if (audioMixLabels.length === 1) {
          audioMap = audioMixLabels[0];
        }

        args.push("-filter_complex", filterParts.join(";"));
        if (currentV !== "0:v") args.push("-map", `[${currentV}]`);
        else args.push("-map", "0:v");
        if (audioMap) args.push("-map", audioMap);
        else args.push("-map", "0:a?");
        args.push(
          "-c:v", "libx264", "-preset", "ultrafast", "-pix_fmt", "yuv420p",
          "-movflags", "+faststart",
          "-shortest",
          outputName,
        );
        await ffmpeg.exec(args);
        finalName = outputName;
      }

      const data = await ffmpeg.readFile(finalName);
      const blob = new Blob([data.buffer || data], { type: "video/mp4" });
      const out = new File([blob], `edited-${Date.now()}.mp4`, { type: "video/mp4" });
      const extras = [];
      if (stickers.length > 0) extras.push(`${stickers.length} sticker${stickers.length > 1 ? "s" : ""}`);
      if (hasMusic) extras.push("music");
      if (hasVoiceover) extras.push("voiceover");
      const extraStr = extras.length ? ` · ${extras.join(" + ")}` : "";
      toast.success(`Exported ${bytesToMB(out.size)} MB · ${sequence.length} clip${sequence.length > 1 ? "s" : ""}${extraStr}`);
      api.post("/studio/analytics/event", { event: "export_completed", payload: { kind: "video", clips: sequence.length, size_mb: Number(bytesToMB(out.size)), has_stickers: hasStickers, has_music: hasMusic, has_voiceover: hasVoiceover } }).catch(() => {});
      // Persist export history (silent — never blocks the download flow)
      try {
        const totalDur = sequence.reduce((sum, c) => sum + Math.max(0, (c.trimEnd || 0) - (c.trimStart || 0)), 0);
        api.post("/studio/exports", {
          draft_id: draftId || undefined,
          kind: "video",
          title: file.name,
          duration_s: totalDur || undefined,
          filesize: out.size,
          dimensions: sequence.length > 1 ? "1080x1920" : (naturalW && naturalH ? `${naturalW}x${naturalH}` : undefined),
        }).catch(() => {});
      } catch { /* noop */ }
      onExport?.(out, { caption, subs });
    } catch (e) {
      console.error(e);
      toast.error("Export failed — try shorter clips or simpler edits");
    } finally { setExporting(false); setProgress(0); }
  };

  const exportImage = async () => {
    setExporting(true);
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      let sw = naturalW, sh = naturalH, sx = 0, sy = 0;
      if (aspectRatio) {
        const sourceRatio = naturalW / naturalH;
        if (aspectRatio > sourceRatio) { sh = Math.floor(naturalW / aspectRatio); sy = Math.floor((naturalH - sh) / 2); }
        else { sw = Math.floor(naturalH * aspectRatio); sx = Math.floor((naturalW - sw) / 2); }
      }
      canvas.width = sw; canvas.height = sh;
      // Rotate + flip via canvas transforms
      ctx.save();
      if (rotate || flipH || flipV) {
        ctx.translate(sw / 2, sh / 2);
        if (rotate) ctx.rotate((rotate * Math.PI) / 180);
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
        ctx.translate(-sw / 2, -sh / 2);
      }
      ctx.filter = previewCss || "none";
      ctx.drawImage(imgRef.current, sx, sy, sw, sh, 0, 0, sw, sh);
      ctx.restore();
      // Text — active overlay + previously committed layers
      const drawTextLayer = (content, xPctRaw, yPctRaw, scale, rotDeg, color, bgKind, bgCol) => {
        if (!content || !content.trim()) return;
        ctx.filter = "none";
        const baseFont = Math.max(24, Math.floor(sh / 16));
        const fontSize = Math.max(10, Math.floor(baseFont * (scale || 1)));
        ctx.font = `bold ${fontSize}px Inter, Arial, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const xPct = Math.max(2, Math.min(98, xPctRaw ?? 50)) / 100;
        const yPct = Math.max(4, Math.min(96, yPctRaw ?? 90)) / 100;
        const x = Math.floor(sw * xPct);
        const y = Math.floor(sh * yPct);
        const rot = (rotDeg || 0) * Math.PI / 180;
        const textW = ctx.measureText(content).width;
        const padX = Math.floor(fontSize * 0.35);
        const padY = Math.floor(fontSize * 0.18);
        const boxW = Math.ceil(textW + padX * 2);
        const boxH = Math.ceil(fontSize + padY * 2);
        ctx.save();
        ctx.translate(x, y);
        if (rot) ctx.rotate(rot);
        // --- background ---
        const kind = bgKind || "none";
        if (kind !== "none") {
          const col = bgCol || "#000000";
          if (kind === "solid" || kind === "semi" || kind === "pill") {
            const radius = kind === "pill" ? boxH / 2 : Math.floor(fontSize * 0.2);
            ctx.beginPath();
            const bx = -boxW / 2, by = -boxH / 2;
            const r = Math.min(radius, boxH / 2, boxW / 2);
            ctx.moveTo(bx + r, by);
            ctx.arcTo(bx + boxW, by, bx + boxW, by + boxH, r);
            ctx.arcTo(bx + boxW, by + boxH, bx, by + boxH, r);
            ctx.arcTo(bx, by + boxH, bx, by, r);
            ctx.arcTo(bx, by, bx + boxW, by, r);
            ctx.closePath();
            ctx.fillStyle = col + (kind === "semi" ? "99" : "");
            ctx.fill();
          } else if (kind === "marker") {
            // Marker highlight — a rectangle covering the lower ~40% of the text bounding box
            ctx.fillStyle = col;
            ctx.fillRect(-boxW / 2, boxH * 0.05, boxW, boxH * 0.42);
          }
        }
        // --- text ---
        if (kind === "none") {
          ctx.strokeStyle = "rgba(0,0,0,0.7)";
          ctx.lineWidth = Math.max(3, fontSize / 10);
          ctx.strokeText(content, 0, 0);
        }
        ctx.fillStyle = color || "#FFFFFF";
        ctx.fillText(content, 0, 0);
        ctx.restore();
        ctx.textBaseline = "alphabetic";
      };
      for (const ct of committedTexts) {
        drawTextLayer(ct.content, ct.xPct, ct.yPct, ct.scale, ct.rotate, ct.color, ct.bg, ct.bgColor);
      }
      if (textOverlay && textOverlay.trim()) {
        drawTextLayer(textOverlay, textPosPct?.x, textPosPct?.y, textScale, textRotate, textColor, textBg, textBgColor);
      }
      // Stickers — emoji rendered as canvas text with rotation
      ctx.filter = "none";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (const s of stickers) {
        if (s.type !== "emoji") continue; // text stickers not yet burned into image export
        const px = (s.xPct / 100) * sw;
        const py = (s.yPct / 100) * sh;
        const emojiPx = Math.floor((s.size / 100) * Math.min(sw, sh));
        ctx.font = `${emojiPx}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",Arial`;
        const rot = ((s.rotate || 0) * Math.PI) / 180;
        ctx.save();
        ctx.translate(px, py);
        if (rot) ctx.rotate(rot);
        ctx.fillText(s.emoji, 0, 0);
        ctx.restore();
      }
      ctx.textBaseline = "alphabetic";
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
      const out = new File([blob], `edited-${Date.now()}.jpg`, { type: "image/jpeg" });
      toast.success(`Exported ${bytesToMB(out.size)} MB`);
      api.post("/studio/analytics/event", { event: "export_completed", payload: { kind: "image", size_mb: Number(bytesToMB(out.size)) } }).catch(() => {});
      try {
        api.post("/studio/exports", {
          draft_id: draftId || undefined,
          kind: "image",
          title: file.name,
          filesize: out.size,
          dimensions: canvas.width && canvas.height ? `${canvas.width}x${canvas.height}` : undefined,
        }).catch(() => {});
      } catch { /* noop */ }
      onExport?.(out, { caption });
    } catch (e) {
      console.error(e);
      toast.error("Image export failed");
    } finally { setExporting(false); }
  };
  const doExport = () => (kind === "video" ? exportVideo() : exportImage());

  const reset = () => {
    setAspect("original"); setFilter("none");
    setBrightness(0); setContrast(1); setSaturation(1);
    setSpeed(1); setMuted(false);
    setTrimStart(0); setTrimEnd(Math.min(MAX_VIDEO_SEC, duration || MAX_VIDEO_SEC));
    setTextOverlay(""); setTextPos("bottom"); setTextPosPct({ x: 50, y: 90 }); setTextScale(1); setTextRotate(0); setTextAnim("static"); setTextColor("#FFFFFF"); setCommittedTexts([]); setEditingCommittedId(null);
    setStickers([]);
    setRotate(0); setFlipH(false); setFlipV(false); setReverse(false);
    setVolume(1); setFadeIn(0); setFadeOut(0); setSplits([]);
  };

  // ------------- Dock config -------------
  const dock = [
    ...(kind === "video" ? [{ v: "trim", l: "Trim", icon: Scissors }] : []),
    { v: "template", l: "Template", icon: LayoutTemplate },
    { v: "crop", l: "Crop", icon: Crop },
    { v: "adjust", l: "Adjust", icon: RotateCw },
    { v: "look", l: "Look", icon: Sparkles },
    ...(kind === "video" ? [{ v: "effects", l: "Effects", icon: Blend }] : []),
    ...(kind === "video" ? [{ v: "background", l: "BG remove", icon: Wand2 }] : []),
    { v: "text", l: "Text", icon: Type },
    { v: "sticker", l: "Sticker", icon: Smile },
    ...(kind === "video" ? [{ v: "audio", l: "Audio", icon: Volume2 }] : []),
    { v: "ai", l: "AI", icon: Wand2 },
  ];

  // ------------- Preview overlay classes -------------
  // Returns { padding, borderRadius, background, WebkitBoxDecorationBreak } for a text layer background.
  const bgStyleFor = (kindStr, color) => {
    if (!kindStr || kindStr === "none") return null;
    const c = color || "#000000";
    if (kindStr === "solid")  return { padding: "0.15em 0.6em", borderRadius: "0.35em", background: c, boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" };
    if (kindStr === "semi")   return { padding: "0.15em 0.6em", borderRadius: "0.35em", background: c + "99", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" };
    if (kindStr === "pill")   return { padding: "0.15em 0.7em", borderRadius: "999px", background: c, boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" };
    if (kindStr === "marker") return { padding: "0.05em 0.2em", borderRadius: 0, background: `linear-gradient(transparent 55%, ${c} 55%, ${c} 92%, transparent 92%)`, boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" };
    return null;
  };
  const overlayAnimCls = textAnim === "fade" ? "animate-fade-up"
    : textAnim === "pop" ? "editor-anim-pop"
    : textAnim === "slide" ? "editor-anim-slide"
    : textAnim === "bounce" ? "editor-anim-bounce"
    : textAnim === "glow" ? "editor-anim-glow"
    : textAnim === "typewriter" ? "editor-anim-type"
    : "";

  const containerAspect = aspectRatio || (naturalW && naturalH ? `${naturalW}/${naturalH}` : "auto");

  return createPortal((
    <div className="fixed inset-0 z-[70] bg-[#050507] flex flex-col text-white pt-[env(safe-area-inset-top)]" data-testid="media-editor">
      {/* Inline animations for text overlay */}
      <style>{`
        @keyframes editorPop { 0% { transform: scale(0.6); opacity: 0 } 60% { transform: scale(1.12); opacity: 1 } 100% { transform: scale(1) } }
        @keyframes editorSlide { 0% { transform: translateY(30%); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
        @keyframes editorBounce { 0% { transform: translateY(-40%) scale(0.6); opacity: 0 }
                                  40% { transform: translateY(8%) scale(1.05); opacity: 1 }
                                  65% { transform: translateY(-4%) scale(0.97); }
                                  100% { transform: translateY(0) scale(1); } }
        @keyframes editorGlow { 0%,100% { text-shadow: 0 0 8px currentColor, 0 0 22px currentColor, 0 2px 12px rgba(0,0,0,0.9); }
                                50% { text-shadow: 0 0 16px currentColor, 0 0 44px currentColor, 0 0 80px currentColor, 0 2px 12px rgba(0,0,0,0.9); } }
        @keyframes editorWaveLetter { 0%, 100% { transform: translateY(0); }
                                       50% { transform: translateY(-18%); } }
        @keyframes editorType { from { clip-path: inset(0 100% 0 0); }
                                to { clip-path: inset(0 0 0 0); } }
        .editor-anim-pop { animation: editorPop 900ms cubic-bezier(.34,1.56,.64,1) both }
        .editor-anim-slide { animation: editorSlide 700ms ease-out both }
        .editor-anim-bounce { animation: editorBounce 1200ms cubic-bezier(.34,1.56,.64,1) both }
        .editor-anim-glow { animation: editorGlow 1600ms ease-in-out infinite }
        .editor-anim-type { animation: editorType 1400ms steps(24, end) both }
        .editor-wave-letter { display: inline-block; animation: editorWaveLetter 1400ms ease-in-out infinite }
        .sticker-item { touch-action: none; user-select: none; }
      `}</style>

      {/* ---------- Header ---------- */}
      <div className="h-14 px-3 sm:px-4 flex items-center justify-between border-b border-white/5 bg-[#050507]/95 backdrop-blur relative z-20">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            onClick={onCancel}
            className="h-9 w-9 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-300 hover:text-white"
            data-testid="editor-close-btn"
            aria-label="Close editor"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="hidden sm:flex items-center gap-2">
            <div className="h-6 w-6 rounded-md nexus-gradient flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="font-display font-bold tracking-tight">Studio</div>
            <span className="text-[9px] uppercase tracking-widest text-zinc-500 border border-white/10 rounded-full px-2 py-0.5">{kind}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Freemium quota chip */}
          {quota && !quota.is_subscribed && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              className={`hidden sm:flex h-9 px-3 items-center gap-1.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border transition-colors ${
                quota.remaining <= 1
                  ? "bg-red-500/15 border-red-500/40 text-red-100 hover:bg-red-500/25"
                  : "bg-white/[0.03] border-white/10 text-zinc-300 hover:border-white/25 hover:text-white"
              }`}
              data-testid="editor-quota-chip"
              title={`${quota.used} of ${quota.limit} free edits used`}
            >
              <Sparkles className="h-3 w-3 text-nexus-pink" />
              {quota.remaining} / {quota.limit} free
            </button>
          )}
          {quota?.is_subscribed && (
            <div className="hidden sm:flex h-9 px-3 items-center gap-1.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-emerald-500/15 border border-emerald-500/40 text-emerald-100" data-testid="editor-quota-pro">
              <Sparkles className="h-3 w-3" /> Pro
            </div>
          )}
          <button
            onClick={() => { resetStudioTour(); setShowTour(true); }}
            className="h-9 w-9 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white"
            data-testid="editor-tour-btn"
            title="Show the Studio tour"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <button
            onClick={reset}
            className="h-9 w-9 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white"
            data-testid="editor-reset-btn"
            title="Reset all edits"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button
            onClick={() => saveDraft()}
            className="h-9 px-3 rounded-full hover:bg-white/5 text-zinc-300 hover:text-white text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5"
            data-testid="editor-save-draft-btn"
            title={lastSavedAt ? `Last saved ${lastSavedAt.toLocaleTimeString()}` : "Save your progress"}
          >
            <Save className={`h-3.5 w-3.5 ${autoSaving ? "opacity-50" : ""}`} />
            <span className="hidden sm:inline">
              {autoSaving ? "Saving…" : (lastSavedAt ? "Saved" : "Draft")}
            </span>
          </button>
          <Button
            onClick={doExport}
            disabled={exporting}
            className="nexus-gradient text-white border-0 rounded-full h-9 px-3 sm:px-4 font-semibold disabled:opacity-60 text-xs sm:text-sm"
            data-testid="editor-export-btn"
          >
            {exporting
              ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> {Math.round(progress * 100)}%</>
              : <><Download className="h-3.5 w-3.5 sm:mr-1.5" /> <span className="hidden sm:inline">Export</span></>}
          </Button>
        </div>
      </div>

      {/* ---------- Preview ---------- */}
      <div className="flex-1 min-h-0 flex items-center justify-center p-2 sm:p-6 bg-[#050507]">
        <div
          ref={previewRef}
          className="relative max-h-full max-w-full overflow-hidden rounded-3xl shadow-[0_30px_120px_rgba(0,0,0,0.7)] bg-black"
          style={{ aspectRatio: containerAspect, maxHeight: "100%", maxWidth: "100%" }}
          data-testid="editor-preview-frame"
          data-editor-canvas
          onPointerDown={(e) => {
            if (e.target === e.currentTarget) setSelectedStickerId(null);
          }}
        >
          {kind === "video" ? (
            <video
              key={activeClipId}
              ref={videoRef}
              src={activeSrc}
              onLoadedMetadata={onVideoLoaded}
              onDurationChange={onDurationOrDataChange}
              onLoadedData={onDurationOrDataChange}
              muted={muted}
              controls
              playsInline
              className="block w-full h-full object-cover"
              style={{ filter: previewCss, transform: previewTransform }}
              data-testid="editor-video-preview"
            />
          ) : (
            <img
              ref={imgRef}
              src={url}
              onLoad={onImageLoaded}
              alt=""
              className="block w-full h-full object-cover"
              style={{ filter: previewCss, transform: previewTransform }}
              data-testid="editor-image-preview"
            />
          )}

          {/* Committed text layers. Caption layers (with time-gates) are hidden from preview
              because they'd all overlap — they're time-gated correctly in the final export. */}
          {committedTexts.filter((ct) => !ct.isCaption).map((ct) => (
            <div
              key={ct.id}
              className="absolute select-none group"
              style={{
                left: `${ct.xPct}%`,
                top: `${ct.yPct}%`,
                transform: `translate(-50%, -50%) rotate(${ct.rotate || 0}deg) scale(${ct.scale || 1})`,
                transformOrigin: "center center",
                cursor: "pointer",
                zIndex: 28,
                touchAction: "none",
              }}
              onPointerDown={(e) => {
                if (e.target.closest("[data-committed-delete]")) return;
                e.preventDefault();
                e.stopPropagation();
                const parent = e.currentTarget.parentElement;
                const rect = parent.getBoundingClientRect();
                const startX = e.clientX;
                const startY = e.clientY;
                const startPct = { x: ct.xPct, y: ct.yPct };
                const move = (ev) => {
                  const dxPct = ((ev.clientX - startX) / rect.width) * 100;
                  const dyPct = ((ev.clientY - startY) / rect.height) * 100;
                  setCommittedTexts((arr) => arr.map((it) => it.id === ct.id ? {
                    ...it,
                    xPct: Math.max(2, Math.min(98, startPct.x + dxPct)),
                    yPct: Math.max(4, Math.min(96, startPct.y + dyPct)),
                  } : it));
                };
                const up = () => {
                  window.removeEventListener("pointermove", move);
                  window.removeEventListener("pointerup", up);
                };
                window.addEventListener("pointermove", move);
                window.addEventListener("pointerup", up);
              }}
              onDoubleClick={(e) => {
                // Double-click to load this layer back into the editor
                e.stopPropagation();
                editCommittedText(ct.id);
              }}
              data-testid={`committed-text-${ct.id}`}
              title="Drag to move · double-click to edit · hover × to delete"
            >
              <div className="relative">
                <div
                  className="text-center font-display font-black text-2xl sm:text-3xl md:text-5xl px-4 whitespace-nowrap"
                  style={{
                    color: ct.color,
                    textShadow: (!ct.bg || ct.bg === "none") ? "0 2px 12px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)" : "none",
                    ...(bgStyleFor(ct.bg, ct.bgColor) || {}),
                    display: (ct.bg && ct.bg !== "none") ? "inline-block" : "block",
                  }}
                >
                  {ct.content}
                </div>
                <button
                  type="button"
                  data-committed-delete
                  data-testid={`committed-text-delete-${ct.id}`}
                  aria-label="Delete text layer"
                  onClick={(e) => { e.stopPropagation(); setCommittedTexts((a) => a.filter((t) => t.id !== ct.id)); }}
                  className="absolute -top-3 -right-3 h-6 w-6 rounded-full bg-black/85 border border-white/25 text-white text-sm leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/80 pointer-events-auto"
                  style={{ fontSize: "14px" }}
                >
                  ×
                </button>
              </div>
            </div>
          ))}

          {/* Text overlay — draggable, resizable, rotatable */}
          {textOverlay && (
            <div
              key={`${textOverlay}-${textAnim}-${textColor}`}
              className="group absolute select-none"
              style={{
                left: `${textPosPct.x}%`,
                top: `${textPosPct.y}%`,
                transform: `translate(-50%, -50%) rotate(${textRotate}deg) scale(${textScale})`,
                transformOrigin: "center center",
                cursor: textDragging ? "grabbing" : "grab",
                touchAction: "none",
                zIndex: 30,
              }}
              onPointerDown={(e) => {
                if (e.target.closest("[data-editor-handle]")) return;
                e.preventDefault();
                e.stopPropagation();
                const parent = e.currentTarget.parentElement;
                const rect = parent.getBoundingClientRect();
                const startX = e.clientX;
                const startY = e.clientY;
                const startPct = { ...textPosPct };
                setTextDragging(true);
                const move = (ev) => {
                  const dxPct = ((ev.clientX - startX) / rect.width) * 100;
                  const dyPct = ((ev.clientY - startY) / rect.height) * 100;
                  setTextPosPct({
                    x: Math.max(2, Math.min(98, startPct.x + dxPct)),
                    y: Math.max(4, Math.min(96, startPct.y + dyPct)),
                  });
                };
                const up = () => {
                  setTextDragging(false);
                  window.removeEventListener("pointermove", move);
                  window.removeEventListener("pointerup", up);
                  window.removeEventListener("pointercancel", up);
                };
                window.addEventListener("pointermove", move);
                window.addEventListener("pointerup", up);
                window.addEventListener("pointercancel", up);
              }}
              data-testid="editor-text-overlay-draggable"
              title="Drag to reposition · corners resize · top handle rotates"
            >
              <div className="relative">
                <div
                  className={`text-center font-display font-black text-2xl sm:text-3xl md:text-5xl px-4 whitespace-nowrap ${overlayAnimCls}`}
                  style={{
                    color: textColor,
                    textShadow: (!textBg || textBg === "none") ? "0 2px 12px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.9)" : "none",
                    ...(bgStyleFor(textBg, textBgColor) || {}),
                    display: (textBg && textBg !== "none") ? "inline-block" : "block",
                  }}
                >
                  {textAnim === "wave" ? (
                    <span>
                      {Array.from(textOverlay).map((ch, i) => (
                        <span
                          key={i}
                          className="editor-wave-letter"
                          style={{ animationDelay: `${i * 60}ms` }}
                        >
                          {ch === " " ? "\u00A0" : ch}
                        </span>
                      ))}
                    </span>
                  ) : textOverlay}
                </div>

                {/* Selection outline + handles (shown on hover / focus-within) */}
                <div className="absolute inset-0 border border-dashed border-nexus-pink/60 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

                {/* Rotation handle — line + circle above the text */}
                <div className="absolute left-1/2 -top-9 w-px h-6 bg-nexus-pink/70 -translate-x-1/2 opacity-0 group-hover:opacity-100 pointer-events-none" />
                <button
                  type="button"
                  data-editor-handle
                  data-testid="editor-text-rotate-handle"
                  aria-label="Rotate text"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const canvasEl = e.currentTarget.closest("[data-editor-canvas]") || e.currentTarget.parentElement.parentElement.parentElement;
                    const rect = canvasEl.getBoundingClientRect();
                    const cx = rect.left + rect.width * (textPosPct.x / 100);
                    const cy = rect.top + rect.height * (textPosPct.y / 100);
                    const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
                    const startRotate = textRotate;
                    const move = (ev) => {
                      const angle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
                      let next = startRotate + (angle - startAngle);
                      if (ev.shiftKey) next = Math.round(next / 15) * 15;
                      // Clamp to -180..180
                      while (next > 180) next -= 360;
                      while (next < -180) next += 360;
                      setTextRotate(next);
                    };
                    const up = () => {
                      window.removeEventListener("pointermove", move);
                      window.removeEventListener("pointerup", up);
                    };
                    window.addEventListener("pointermove", move);
                    window.addEventListener("pointerup", up);
                  }}
                  className="absolute left-1/2 -top-14 -translate-x-1/2 h-4 w-4 rounded-full bg-nexus-pink border-2 border-white shadow-[0_2px_10px_rgba(255,47,110,0.5)] opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing pointer-events-auto"
                  title="Drag to rotate · Shift for 15° snap"
                />

                {/* Corner resize handles */}
                {[
                  { key: "tl", cls: "-top-1.5 -left-1.5 cursor-nwse-resize" },
                  { key: "tr", cls: "-top-1.5 -right-1.5 cursor-nesw-resize" },
                  { key: "bl", cls: "-bottom-1.5 -left-1.5 cursor-nesw-resize" },
                  { key: "br", cls: "-bottom-1.5 -right-1.5 cursor-nwse-resize" },
                ].map((h) => (
                  <button
                    key={h.key}
                    type="button"
                    data-editor-handle
                    data-testid={`editor-text-resize-${h.key}`}
                    aria-label={`Resize text ${h.key}`}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Use the wrapper's own bounding box for a stable pivot regardless of rotation
                      const wrapper = e.currentTarget.parentElement.parentElement;
                      const wrapRect = wrapper.getBoundingClientRect();
                      const cx = wrapRect.left + wrapRect.width / 2;
                      const cy = wrapRect.top + wrapRect.height / 2;
                      const initialDist = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
                      const startScale = textScale;
                      const move = (ev) => {
                        const d = Math.hypot(ev.clientX - cx, ev.clientY - cy) || 1;
                        const next = Math.max(0.4, Math.min(3, startScale * (d / initialDist)));
                        setTextScale(next);
                      };
                      const up = () => {
                        window.removeEventListener("pointermove", move);
                        window.removeEventListener("pointerup", up);
                      };
                      window.addEventListener("pointermove", move);
                      window.addEventListener("pointerup", up);
                    }}
                    className={`absolute ${h.cls} h-3 w-3 rounded-full bg-white border-2 border-nexus-pink shadow-[0_2px_8px_rgba(0,0,0,0.4)] opacity-0 group-hover:opacity-100 pointer-events-auto`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Stickers — draggable, resizable, rotatable; hover shows × delete, click to select */}
          {stickers.map((s) => {
            const isSel = selectedStickerId === s.id;
            return (
              <div
                key={s.id}
                className={`sticker-item absolute -translate-x-1/2 -translate-y-1/2 group`}
                style={{
                  left: `${s.xPct}%`, top: `${s.yPct}%`,
                  fontSize: `clamp(28px, ${s.size}cqmin, 160px)`,
                  filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.6))",
                  touchAction: "none",
                  cursor: "grab",
                  zIndex: isSel ? 25 : 20,
                  transform: `translate(-50%, -50%) rotate(${s.rotate || 0}deg)`,
                }}
                onPointerDown={(e) => {
                  if (e.target.closest("[data-sticker-delete]") || e.target.closest("[data-editor-handle]")) return;
                  e.preventDefault();
                  e.stopPropagation();
                  const parent = e.currentTarget.parentElement;
                  const rect = parent.getBoundingClientRect();
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const startPct = { x: s.xPct, y: s.yPct };
                  setSelectedStickerId(s.id);
                  const move = (ev) => {
                    const dxPct = ((ev.clientX - startX) / rect.width) * 100;
                    const dyPct = ((ev.clientY - startY) / rect.height) * 100;
                    setStickers((arr) => arr.map((it) => it.id === s.id ? {
                      ...it,
                      xPct: Math.max(3, Math.min(97, startPct.x + dxPct)),
                      yPct: Math.max(3, Math.min(97, startPct.y + dyPct)),
                    } : it));
                  };
                  const up = () => {
                    window.removeEventListener("pointermove", move);
                    window.removeEventListener("pointerup", up);
                    window.removeEventListener("pointercancel", up);
                  };
                  window.addEventListener("pointermove", move);
                  window.addEventListener("pointerup", up);
                  window.addEventListener("pointercancel", up);
                }}
                data-testid={`sticker-${s.id}`}
                title="Drag to move · corners resize · top dot rotates · × deletes"
              >
                <div className={`relative ${isSel ? "outline outline-1 outline-dashed outline-nexus-pink/60 rounded" : ""}`}>
                  {s.type === "text" ? (
                    <TextStickerBadge text={s.text} shape={s.shape} color={s.color} />
                  ) : s.emoji}

                  {/* Delete × */}
                  <button
                    type="button"
                    data-sticker-delete
                    data-testid={`sticker-delete-${s.id}`}
                    aria-label="Delete sticker"
                    onClick={(e) => { e.stopPropagation(); removeSticker(s.id); if (selectedStickerId === s.id) setSelectedStickerId(null); }}
                    className="absolute -top-3 -right-3 h-6 w-6 rounded-full bg-black/85 border border-white/25 text-white text-sm leading-none flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/80 pointer-events-auto"
                    style={{ fontSize: "14px" }}
                  >
                    ×
                  </button>

                  {/* Rotation handle */}
                  <div className="absolute left-1/2 -top-8 w-px h-5 bg-nexus-pink/70 -translate-x-1/2 opacity-0 group-hover:opacity-100 pointer-events-none" />
                  <button
                    type="button"
                    data-editor-handle
                    data-testid={`sticker-rotate-${s.id}`}
                    aria-label="Rotate sticker"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const canvasEl = e.currentTarget.closest("[data-editor-canvas]");
                      if (!canvasEl) return;
                      const rect = canvasEl.getBoundingClientRect();
                      const cx = rect.left + rect.width * (s.xPct / 100);
                      const cy = rect.top + rect.height * (s.yPct / 100);
                      const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
                      const startRotate = s.rotate || 0;
                      setSelectedStickerId(s.id);
                      const move = (ev) => {
                        const angle = Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180 / Math.PI;
                        let next = startRotate + (angle - startAngle);
                        if (ev.shiftKey) next = Math.round(next / 15) * 15;
                        while (next > 180) next -= 360;
                        while (next < -180) next += 360;
                        setStickers((arr) => arr.map((it) => it.id === s.id ? { ...it, rotate: next } : it));
                      };
                      const up = () => {
                        window.removeEventListener("pointermove", move);
                        window.removeEventListener("pointerup", up);
                      };
                      window.addEventListener("pointermove", move);
                      window.addEventListener("pointerup", up);
                    }}
                    className="absolute left-1/2 -top-12 -translate-x-1/2 h-4 w-4 rounded-full bg-nexus-pink border-2 border-white shadow-[0_2px_10px_rgba(255,47,110,0.5)] opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing pointer-events-auto"
                    title="Drag to rotate · Shift for 15° snap"
                  />

                  {/* Corner resize handles */}
                  {[
                    { key: "tl", cls: "-top-1.5 -left-1.5 cursor-nwse-resize" },
                    { key: "tr", cls: "-top-1.5 -right-1.5 cursor-nesw-resize" },
                    { key: "bl", cls: "-bottom-1.5 -left-1.5 cursor-nesw-resize" },
                    { key: "br", cls: "-bottom-1.5 -right-1.5 cursor-nwse-resize" },
                  ].map((h) => (
                    <button
                      key={h.key}
                      type="button"
                      data-editor-handle
                      data-testid={`sticker-resize-${s.id}-${h.key}`}
                      aria-label={`Resize sticker ${h.key}`}
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const wrapper = e.currentTarget.parentElement.parentElement;
                        const wrapRect = wrapper.getBoundingClientRect();
                        const cx = wrapRect.left + wrapRect.width / 2;
                        const cy = wrapRect.top + wrapRect.height / 2;
                        const initialDist = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
                        const startSize = s.size;
                        setSelectedStickerId(s.id);
                        const move = (ev) => {
                          const d = Math.hypot(ev.clientX - cx, ev.clientY - cy) || 1;
                          const next = Math.max(6, Math.min(80, startSize * (d / initialDist)));
                          setStickers((arr) => arr.map((it) => it.id === s.id ? { ...it, size: next } : it));
                        };
                        const up = () => {
                          window.removeEventListener("pointermove", move);
                          window.removeEventListener("pointerup", up);
                        };
                        window.addEventListener("pointermove", move);
                        window.addEventListener("pointerup", up);
                      }}
                      className={`absolute ${h.cls} h-3 w-3 rounded-full bg-white border-2 border-nexus-pink shadow-[0_2px_8px_rgba(0,0,0,0.4)] opacity-0 group-hover:opacity-100 pointer-events-auto`}
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {/* Export overlay */}
          {exporting && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-nexus-pink" />
              <div className="text-sm">Rendering… {Math.round(progress * 100)}%</div>
              <div className="w-40 h-1 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full nexus-gradient" style={{ width: `${Math.round(progress * 100)}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ---------- CapCut-style timeline (video only) ---------- */}
      {kind === "video" && activeDuration > 0 && (
        <Timeline
          key={activeClipId}
          videoRef={videoRef}
          duration={activeDuration}
          trimStart={activeTrimStart}
          trimEnd={activeTrimEnd}
          maxDurationSec={MAX_VIDEO_SEC}
          onTrimStart={setActiveTrimStart}
          onTrimEnd={setActiveTrimEnd}
          src={activeSrc}
        />
      )}

      {/* ---------- Multi-clip strip with reorder + transitions (video only) ---------- */}
      {kind === "video" && (
        <div className="px-3 py-2 bg-[#08080B] border-t border-white/5 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="editor-clip-strip">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500 shrink-0 pr-1">
            Clips <span className="text-white font-semibold">{1 + extraClips.length}</span>
            <span className="ml-2 text-nexus-pink">{totalEditDuration().toFixed(1)}s / {MAX_VIDEO_SEC}s</span>
          </div>
          {extraClips.length > 0 && (
            <button
              onClick={() => setLoopPlayback((v) => !v)}
              className={`shrink-0 h-7 px-2.5 rounded-lg text-[10px] uppercase tracking-widest font-semibold border flex items-center gap-1 transition-colors ${
                loopPlayback
                  ? "bg-nexus-pink/15 border-nexus-pink/40 text-white"
                  : "bg-white/[0.03] border-white/10 text-zinc-400 hover:text-white hover:border-white/25"
              }`}
              data-testid="editor-loop-toggle"
              title={loopPlayback ? "Loop is on — sequence replays automatically" : "Turn on loop to replay the sequence end-to-end"}
            >
              <Repeat className="h-3 w-3" /> Loop {loopPlayback ? "on" : "off"}
            </button>
          )}

          {kind === "video" && (
            <button
              onClick={splitAtPlayhead}
              className="h-7 px-2.5 rounded-md border border-nexus-pink/40 bg-nexus-pink/10 text-[10px] uppercase tracking-widest text-nexus-pink hover:bg-nexus-pink/20 hover:border-nexus-pink/70 flex items-center gap-1 shrink-0"
              data-testid="editor-split-quick-btn"
              title="Cut the active clip at the playhead (like TikTok)"
            >
              <Split className="h-3 w-3" /> Cut
            </button>
          )}

          {/* Primary clip pill (draggable + drop target for swap) */}
          <div
            draggable
            onDragStart={() => setDragClipId("primary")}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onPillDrop("primary")}
            onDragEnd={() => setDragClipId(null)}
            onClick={() => setActiveClipId("primary")}
            className={`glass rounded-lg px-2.5 py-1.5 shrink-0 flex items-center gap-1.5 cursor-grab active:cursor-grabbing border transition-colors ${activeClipId === "primary" ? "border-nexus-pink shadow-[0_0_20px_rgba(255,47,110,0.3)]" : "border-white/10 hover:border-white/25"} ${dragClipId === "primary" ? "opacity-40" : ""}`}
            data-testid="clip-pill-primary"
          >
            <span className="text-[10px] uppercase tracking-widest text-nexus-pink">Main</span>
            <span className="text-[10px] text-zinc-300 tabular-nums">{Math.max(0, trimEnd - trimStart).toFixed(1)}s</span>
            {filter && filter !== "none" && (
              <span className="text-[9px] uppercase tracking-widest text-zinc-400 border border-white/10 rounded-full px-1.5 py-0.5" data-testid="clip-filter-primary">{filterLabel(filter)}</span>
            )}
            {muted ? <VolumeX className="h-3 w-3 text-red-300" /> : (volume !== 1 && <Volume2 className="h-3 w-3 text-nexus-blue" />)}
          </div>

          {extraClips.map((c, i) => (
            <div key={c.id} className="flex items-center gap-1 shrink-0">
              {/* Transition selector BEFORE this extra clip (index = i, sits between primary/prev and this one) */}
              <button
                onClick={() => cycleTransition(i)}
                className="h-7 min-w-[46px] px-1.5 rounded-md border border-white/10 bg-white/[0.03] text-[9px] uppercase tracking-widest text-zinc-300 hover:text-white hover:border-nexus-pink/40 flex items-center justify-center gap-1"
                data-testid={`transition-${i}`}
                title={`Transition: ${transitionLabel(transitions[i] || "none")}`}
              >
                <span className="text-nexus-pink text-sm leading-none">{transitionIcon(transitions[i] || "none")}</span>
                {transitionLabel(transitions[i] || "none")}
              </button>

              {/* Draggable/selectable extra clip pill */}
              <div className="relative">
                <div
                  draggable
                  onDragStart={() => setDragClipId(c.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onPillDrop(c.id)}
                  onDragEnd={() => setDragClipId(null)}
                  onClick={() => setActiveClipId(c.id)}
                  className={`glass rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 cursor-grab active:cursor-grabbing border transition-all ${
                    activeClipId === c.id ? "border-nexus-blue shadow-[0_0_20px_rgba(59,130,246,0.3)]" : "border-white/10 hover:border-white/25"
                  } ${dragClipId === c.id ? "opacity-40" : ""}`}
                  data-testid={`extra-clip-${c.id}`}
                >
                  <span className="text-[10px] uppercase tracking-widest text-nexus-blue">Clip {i + 2}</span>
                  <span className="text-[10px] text-zinc-300 tabular-nums">{Math.max(0, c.trimEnd - c.trimStart).toFixed(1)}s</span>
                  {c.filter && c.filter !== "none" && (
                    <span className="text-[9px] uppercase tracking-widest text-zinc-400 border border-white/10 rounded-full px-1.5 py-0.5" data-testid={`clip-filter-${c.id}`}>{filterLabel(c.filter)}</span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); setClipAudioPopover(clipAudioPopover === c.id ? null : c.id); }}
                    className={`ml-0.5 ${c.muted ? "text-red-300" : (c.volume !== 1 ? "text-nexus-blue" : "text-zinc-500")} hover:text-white`}
                    title="Audio for this clip"
                    data-testid={`clip-audio-${c.id}`}
                  >
                    {c.muted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeExtraClip(c.id); if (activeClipId === c.id) setActiveClipId("primary"); }}
                    className="text-zinc-500 hover:text-red-300 ml-0.5"
                    title="Remove clip"
                    data-testid={`remove-clip-${c.id}`}
                    aria-label="Remove clip"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                {clipAudioPopover === c.id && (
                  <div
                    className="absolute bottom-full left-0 mb-2 w-52 glass rounded-xl p-3 shadow-[0_10px_40px_rgba(0,0,0,0.6)] z-30 space-y-2"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`clip-audio-popover-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">Clip {i + 2} audio</span>
                      <button onClick={() => setClipAudioPopover(null)} className="text-zinc-500 hover:text-white"><X className="h-3 w-3" /></button>
                    </div>
                    <button
                      onClick={() => setExtraMuted(c.id, !c.muted)}
                      className={`w-full h-8 rounded-lg text-[11px] font-semibold border flex items-center justify-center gap-1.5 ${c.muted ? "bg-red-500/15 text-red-100 border-red-500/40" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
                    >
                      {c.muted ? <><VolumeX className="h-3.5 w-3.5" /> Muted</> : <><Volume2 className="h-3.5 w-3.5" /> Sound on</>}
                    </button>
                    {!c.muted && (
                      <div>
                        <div className="flex justify-between text-[10px] text-zinc-400 mb-1">
                          <span>Volume</span>
                          <span className="tabular-nums">{Math.round((c.volume ?? 1) * 100)}%</span>
                        </div>
                        <Slider value={[c.volume ?? 1]} onValueChange={(v) => setExtraVolume(c.id, v[0])} min={0} max={2} step={0.05} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {(1 + extraClips.length) < 3 && (
            <>
              <input
                ref={clipFileInputRef}
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) addClipFile(f); }}
                data-testid="editor-add-clip-input"
              />
              <button
                onClick={() => clipFileInputRef.current?.click()}
                className="shrink-0 h-7 px-3 rounded-lg border border-dashed border-white/20 text-[10px] uppercase tracking-widest text-zinc-300 hover:text-white hover:border-white/40 flex items-center gap-1"
                data-testid="editor-add-clip-btn"
              >
                + Add clip
              </button>
            </>
          )}
        </div>
      )}

      {/* ---------- Audio timeline lanes (video only, once we know the total duration) ---------- */}
      {kind === "video" && totalEditDuration() > 0 && (musicFile || voiceoverFile || fxSlots.length > 0 || activeTool === "audio") && (
        <AudioLanes
          totalDurationSec={totalEditDuration()}
          hasClipAudio={!muted}
          clipMuted={muted}
          hasMusic={!!musicFile}
          musicStart={musicStart}
          musicVolume={musicVolume}
          musicName={musicName}
          hasVoiceover={!!voiceoverFile}
          voiceoverStart={voiceoverStart}
          voiceoverVolume={voiceoverVolume}
          voiceoverName={voiceoverName}
          fxSlots={fxSlots}
          onChangeVoiceoverStart={setVoiceoverStart}
          onChangeFxAt={(id, atSec) => updateFxSlot(id, { atSec })}
        />
      )}

      {/* ---------- Bottom sheet (tool content) ---------- */}
      {activeTool && (
        <div
          className="border-t border-white/5 bg-[#0A0A10] max-h-[42vh] overflow-y-auto animate-fade-up"
          data-testid={`editor-sheet-${activeTool}`}
        >
          <div className="p-4 sm:p-5 space-y-4 max-w-4xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{dock.find((d) => d.v === activeTool)?.l}</div>
              <button
                onClick={() => setActiveTool(null)}
                className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white"
                data-testid="editor-sheet-done"
              >Done</button>
            </div>

            {activeTool === "trim" && kind === "video" && (
              <div className="space-y-4">
                <p className="text-[11px] text-zinc-500">
                  Drag the pink handles on the timeline below to trim. Tap the strip to scrub.
                </p>
                <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                  <span className="text-nexus-pink font-semibold">{Math.max(0, trimEnd - trimStart).toFixed(2)}s</span>
                  <span>selected · Max {MAX_VIDEO_SEC}s · Source {duration.toFixed(1)}s</span>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Zap className="h-3 w-3" /> Speed</div>
                  <div className="grid grid-cols-4 gap-2">
                    {[0.5, 1, 1.5, 2].map((s) => (
                      <PillButton key={s} active={speed === s} onClick={() => setSpeed(s)} testId={`editor-speed-${s}`}>{s}×</PillButton>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTool === "template" && (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.v}
                      onClick={() => applyTemplate(t)}
                      className="p-3 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent text-left hover:border-nexus-pink/40 hover:bg-white/[0.06] transition-colors group"
                      data-testid={`editor-template-${t.v}`}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-nexus-pink mb-1">{t.aspect} · {t.filter}</div>
                      <div className="font-display font-bold text-sm text-white">{t.l}</div>
                      <div className="text-[10px] text-zinc-500 mt-1 truncate">&ldquo;{t.overlay}&rdquo;</div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-500 mt-3">One-tap presets — tuned for brand collab content.</p>
              </div>
            )}

            {activeTool === "crop" && (
              <div>
                <div className="grid grid-cols-5 gap-2">
                  {ASPECTS.map((a) => (
                    <PillButton key={a.v} active={aspect === a.v} onClick={() => setAspect(a.v)} testId={`editor-aspect-${a.v}`}>{a.l}</PillButton>
                  ))}
                </div>
                <div className="text-[11px] text-zinc-500 mt-3">Cropped from centre · Source {naturalW}×{naturalH}</div>
              </div>
            )}

            {activeTool === "look" && (
              <div className="space-y-5">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                    <span>Preset</span>
                    <span className="text-nexus-pink normal-case tracking-normal">
                      · applies to {isActivePrimary ? "Main clip" : `Clip ${extraClips.findIndex((c) => c.id === activeClipId) + 2}`}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {FILTER_PRESETS.map((f) => (
                      <PillButton
                        key={f.v}
                        active={activeFilter === f.v}
                        onClick={() => {
                          setActiveFilter(f.v);
                          if (f.v !== "none") {
                            api.post("/studio/analytics/event", { event: "filter_applied", payload: { filter: f.v, per_clip: !isActivePrimary } }).catch(() => {});
                          }
                        }}
                        testId={`editor-filter-${f.v}`}
                      >{f.l}</PillButton>
                    ))}
                  </div>
                </div>
                {[
                  { l: "Brightness", v: brightness, set: setBrightness, min: -0.5, max: 0.5, step: 0.02 },
                  { l: "Contrast", v: contrast, set: setContrast, min: 0.5, max: 1.5, step: 0.02 },
                  { l: "Saturation", v: saturation, set: setSaturation, min: 0, max: 2, step: 0.05 },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="flex justify-between text-[11px] text-zinc-400 mb-1"><span>{s.l}</span><span className="tabular-nums">{s.v.toFixed(2)}</span></div>
                    <Slider value={[s.v]} onValueChange={(v) => s.set(v[0])} min={s.min} max={s.max} step={s.step} />
                  </div>
                ))}
              </div>
            )}

            {activeTool === "text" && (
              <div className="space-y-4">
                {/* Committed layer chips + Add new */}
                <div className="flex items-center gap-2 flex-wrap" data-testid="text-layers-bar">
                  {committedTexts.some((t) => t.isCaption) && (
                    <div className="group inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-1 bg-nexus-blue/10 border border-nexus-blue/40" data-testid="caption-group-chip">
                      <span className="text-[11px] text-nexus-blue">
                        ✨ {committedTexts.filter((t) => t.isCaption).length} auto captions
                      </span>
                      <button
                        type="button"
                        onClick={() => setCommittedTexts((a) => a.filter((t) => !t.isCaption))}
                        className="h-5 w-5 rounded-full flex items-center justify-center text-nexus-blue hover:text-nexus-pink hover:bg-white/5 text-sm leading-none"
                        aria-label="Clear captions"
                        data-testid="clear-captions-btn"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  {committedTexts.filter((ct) => !ct.isCaption).map((ct) => (
                    <div
                      key={ct.id}
                      className="group inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-1 bg-white/[0.04] border border-white/10 hover:border-nexus-pink/40 transition-colors"
                    >
                      <button
                        type="button"
                        onClick={() => editCommittedText(ct.id)}
                        className="text-[11px] text-zinc-200 max-w-[120px] truncate"
                        data-testid={`edit-committed-text-${ct.id}`}
                        title="Edit this layer"
                      >
                        {ct.content}
                      </button>
                      <button
                        type="button"
                        onClick={() => setCommittedTexts((a) => a.filter((t) => t.id !== ct.id))}
                        className="h-5 w-5 rounded-full flex items-center justify-center text-zinc-400 hover:text-nexus-pink hover:bg-white/5 text-sm leading-none"
                        aria-label="Delete text layer"
                        data-testid={`delete-committed-text-${ct.id}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addNewTextLayer}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-3 py-1 rounded-full border border-dashed border-nexus-pink/40 text-nexus-pink hover:bg-nexus-pink/5 transition-colors"
                    data-testid="add-text-layer-btn"
                    title={textOverlay.trim() ? "Save current + add another" : "Add a text layer"}
                  >
                    + Text layer
                  </button>
                  {kind === "video" && file && (
                    <button
                      type="button"
                      onClick={async () => {
                        toast.info("Transcribing… this can take a minute for long clips");
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const { data } = await api.post("/studio/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          const segs = data.segments || [];
                          if (!segs.length) { toast.error("No speech detected"); return; }
                          const newLayers = segs.map((s, i) => ({
                            id: `cap-${Date.now()}-${i}`,
                            content: (s.text || "").trim().slice(0, 80),
                            xPct: 50, yPct: 85,
                            scale: 1, rotate: 0,
                            anim: "fade", color: "#FFFFFF",
                            bg: "solid", bgColor: "#000000",
                            startTime: Number(s.start || 0),
                            endTime: Number(s.end || 0),
                            isCaption: true,
                          }));
                          setCommittedTexts((prev) => [...prev.filter((t) => !t.isCaption), ...newLayers]);
                          toast.success(`Added ${newLayers.length} caption${newLayers.length === 1 ? "" : "s"} · time-gated in export`);
                        } catch (err) {
                          toast.error(formatApiError(err.response?.data?.detail) || "Transcription failed");
                        }
                      }}
                      className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest px-3 py-1 rounded-full border border-nexus-blue/40 text-nexus-blue hover:bg-nexus-blue/5 transition-colors"
                      data-testid="auto-captions-btn"
                      title="Transcribe speech with Whisper and add time-gated captions"
                    >
                      ✨ Auto Captions
                    </button>
                  )}
                  {committedTexts.length > 0 && (
                    <span className="text-[10px] text-zinc-500 ml-auto" data-testid="text-layers-count">
                      {committedTexts.length + (textOverlay ? 1 : 0)} layer{(committedTexts.length + (textOverlay ? 1 : 0)) === 1 ? "" : "s"}
                    </span>
                  )}
                </div>

                <Input
                  value={textOverlay}
                  onChange={(e) => setTextOverlay(e.target.value.slice(0, 60))}
                  placeholder="POV: your first collab"
                  className="bg-white/[0.03] border-white/10 text-white text-base"
                  maxLength={60}
                  data-testid="editor-text-input"
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Position</div>
                      <div className="text-[10px] text-zinc-500">Or drag on canvas</div>
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { v: "top",    l: "Top", pct: { x: 50, y: 12 } },
                        { v: "middle", l: "Mid", pct: { x: 50, y: 50 } },
                        { v: "bottom", l: "Btm", pct: { x: 50, y: 90 } },
                      ].map((p) => (
                        <PillButton
                          key={p.v}
                          active={textPos === p.v}
                          onClick={() => { setTextPos(p.v); setTextPosPct(p.pct); }}
                          testId={`editor-text-pos-${p.v}`}
                        >{p.l}</PillButton>
                      ))}
                    </div>
                  </div>

                  {/* Size + Rotation sliders — mirror the on-canvas handles */}
                  <div className="space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                        <span>Size</span>
                        <span className="tabular-nums text-zinc-400 normal-case tracking-normal">{textScale.toFixed(2)}×</span>
                      </div>
                      <input
                        type="range"
                        min="0.4" max="3" step="0.05"
                        value={textScale}
                        onChange={(e) => setTextScale(parseFloat(e.target.value))}
                        className="w-full accent-nexus-pink"
                        data-testid="editor-text-scale-slider"
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                        <span>Rotation</span>
                        <span className="tabular-nums text-zinc-400 normal-case tracking-normal">{Math.round(textRotate)}°</span>
                      </div>
                      <input
                        type="range"
                        min="-180" max="180" step="1"
                        value={textRotate}
                        onChange={(e) => setTextRotate(parseInt(e.target.value, 10))}
                        className="w-full accent-nexus-pink"
                        data-testid="editor-text-rotate-slider"
                      />
                    </div>
                    {(textScale !== 1 || textRotate !== 0 || textPosPct.x !== 50 || (textPosPct.y !== 90 && textPos === "bottom")) && (
                      <button
                        onClick={() => {
                          setTextScale(1); setTextRotate(0);
                          setTextPosPct(textPos === "top" ? { x: 50, y: 12 } : textPos === "middle" ? { x: 50, y: 50 } : { x: 50, y: 90 });
                        }}
                        className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white underline underline-offset-2"
                        data-testid="editor-text-transform-reset"
                      >
                        Reset transform
                      </button>
                    )}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Animation</div>
                    <div className="grid grid-cols-4 gap-1.5">
                      {TEXT_ANIMATIONS.map((a) => (
                        <PillButton key={a.v} active={textAnim === a.v} onClick={() => setTextAnim(a.v)} testId={`editor-text-anim-${a.v}`}>{a.l}</PillButton>
                      ))}
                    </div>
                    <p className="mt-2 text-[10px] text-zinc-500">
                      {TEXT_ANIMATIONS.find((a) => a.v === textAnim)?.desc || ""}
                      {["bounce", "glow", "wave", "typewriter"].includes(textAnim) && (
                        <span className="text-amber-300/70"> · preview only for now — exports as fade-in</span>
                      )}
                    </p>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Colour</div>
                  <div className="flex gap-2 flex-wrap">
                    {TEXT_COLORS.map((c) => (
                      <button
                        key={c}
                        onClick={() => setTextColor(c)}
                        style={{ backgroundColor: c }}
                        className={`h-8 w-8 rounded-full border-2 transition-all ${textColor === c ? "border-white scale-110" : "border-white/20"}`}
                        data-testid={`editor-text-color-${c.slice(1)}`}
                        aria-label={c}
                      />
                    ))}
                  </div>
                </div>

                {/* Background — IG-Stories style pill / highlight box */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Background</div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {[
                      { v: "none",   l: "None" },
                      { v: "solid",  l: "Solid" },
                      { v: "semi",   l: "Semi" },
                      { v: "pill",   l: "Pill" },
                      { v: "marker", l: "Marker" },
                    ].map((b) => (
                      <PillButton
                        key={b.v}
                        active={textBg === b.v}
                        onClick={() => setTextBg(b.v)}
                        testId={`editor-text-bg-${b.v}`}
                      >{b.l}</PillButton>
                    ))}
                  </div>
                  {textBg !== "none" && (
                    <div className="mt-3">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Background colour</div>
                      <div className="flex gap-2 flex-wrap">
                        {TEXT_COLORS.map((c) => (
                          <button
                            key={c}
                            onClick={() => setTextBgColor(c)}
                            style={{ backgroundColor: c }}
                            className={`h-7 w-7 rounded-full border-2 transition-all ${textBgColor === c ? "border-white scale-110" : "border-white/20"}`}
                            data-testid={`editor-text-bg-color-${c.slice(1)}`}
                            aria-label={c}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTool === "sticker" && (
              <div className="space-y-3">
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                  {STICKERS.map((s) => (
                    <button
                      key={s}
                      onClick={() => addSticker(s)}
                      className="aspect-square rounded-xl bg-white/[0.03] border border-white/10 flex items-center justify-center text-3xl hover:bg-white/[0.08] hover:border-nexus-pink/40 hover:scale-105 transition-all"
                      data-testid={`editor-sticker-${s}`}
                      aria-label={`Add sticker ${s}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                {/* Custom text sticker builder */}
                <div className="pt-3 border-t border-white/5 space-y-3">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                    <Type className="h-3 w-3 text-nexus-pink" /> Custom text sticker
                  </div>
                  <Input
                    value={textStickerText}
                    onChange={(e) => setTextStickerText(e.target.value.slice(0, 32))}
                    placeholder="SALE, NEW DROP, £29…"
                    className="bg-white/[0.03] border-white/10 text-white"
                    data-testid="editor-text-sticker-input"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Shape</div>
                      <div className="grid grid-cols-3 gap-1.5">
                        {[{ v: "chip", l: "Chip" }, { v: "banner", l: "Banner" }, { v: "tag", l: "Tag" }].map((sh) => (
                          <PillButton key={sh.v} active={textStickerShape === sh.v} onClick={() => setTextStickerShape(sh.v)} testId={`editor-text-sticker-shape-${sh.v}`}>{sh.l}</PillButton>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Colour</div>
                      <div className="flex gap-1.5 flex-wrap">
                        {["#FF2F6E", "#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#000000"].map((c) => (
                          <button
                            key={c}
                            onClick={() => setTextStickerColor(c)}
                            style={{ backgroundColor: c }}
                            className={`h-7 w-7 rounded-full border-2 transition-all ${textStickerColor === c ? "border-white scale-110" : "border-white/20"}`}
                            data-testid={`editor-text-sticker-color-${c.slice(1)}`}
                            aria-label={c}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  {textStickerText.trim() && (
                    <div className="flex items-center justify-center py-3 rounded-xl bg-black/40 border border-white/10 text-lg font-black">
                      <TextStickerBadge text={textStickerText.trim()} shape={textStickerShape} color={textStickerColor} />
                    </div>
                  )}
                  <Button
                    onClick={() => { addTextSticker(textStickerText, textStickerShape, textStickerColor); setTextStickerText(""); }}
                    disabled={!textStickerText.trim()}
                    className="nexus-gradient text-white border-0 rounded-full w-full h-10 font-semibold disabled:opacity-50"
                    data-testid="editor-text-sticker-add"
                  >
                    <Sparkles className="h-4 w-4 mr-2" /> Add text sticker
                  </Button>
                </div>

                {stickers.length > 0 && (
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                    <span>{stickers.length} on canvas</span>
                    <button
                      onClick={() => setStickers([])}
                      className="text-nexus-pink hover:text-white uppercase tracking-widest text-[10px] font-semibold flex items-center gap-1"
                      data-testid="editor-stickers-clear"
                    >
                      <Trash2 className="h-3 w-3" /> Clear
                    </button>
                    <span className="ml-auto text-zinc-600">Tap a sticker to remove</span>
                  </div>
                )}
                {kind === "video" && (
                  <p className="text-[11px] text-emerald-300/80 bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2">
                    ✓ Stickers now burn into your exported video (post-process pass — adds ~2s to render).
                  </p>
                )}
              </div>
            )}

            {activeTool === "adjust" && (
              <div className="space-y-4" data-testid="editor-panel-adjust">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><RotateCw className="h-3 w-3" /> Rotate</div>
                  <div className="grid grid-cols-4 gap-2">
                    {[0, 90, 180, 270].map((r) => (
                      <PillButton key={r} active={rotate === r} onClick={() => setRotate(r)} testId={`editor-rotate-${r}`}>{r}°</PillButton>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Mirror</div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setFlipH(!flipH)}
                      data-testid="editor-flip-h"
                      className={`h-9 rounded-full text-[11px] font-semibold border transition-all flex items-center justify-center gap-1.5 ${flipH ? "bg-white text-black border-white" : "border-white/10 text-zinc-300 hover:text-white hover:border-white/25"}`}
                    ><FlipHorizontal className="h-3.5 w-3.5" /> Flip H</button>
                    <button
                      onClick={() => setFlipV(!flipV)}
                      data-testid="editor-flip-v"
                      className={`h-9 rounded-full text-[11px] font-semibold border transition-all flex items-center justify-center gap-1.5 ${flipV ? "bg-white text-black border-white" : "border-white/10 text-zinc-300 hover:text-white hover:border-white/25"}`}
                    ><FlipVertical className="h-3.5 w-3.5" /> Flip V</button>
                  </div>
                </div>
                {kind === "video" && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Playback</div>
                    <button
                      onClick={() => setReverse(!reverse)}
                      data-testid="editor-reverse"
                      className={`w-full h-10 rounded-full text-sm font-semibold border transition-all flex items-center justify-center gap-2 ${reverse ? "bg-nexus-pink/20 text-white border-nexus-pink/40" : "border-white/10 text-zinc-300 hover:text-white hover:border-white/25"}`}
                    ><Rewind className="h-4 w-4" /> {reverse ? "Playing reversed" : "Reverse the clip"}</button>
                  </div>
                )}
              </div>
            )}

            {activeTool === "effects" && kind === "video" && (
              <div className="space-y-5" data-testid="editor-panel-effects">
                <div>
                  <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                    <span className="flex items-center gap-1.5"><Blend className="h-3 w-3 text-nexus-pink" /> Fade in</span>
                    <span className="tabular-nums">{fadeIn.toFixed(1)}s</span>
                  </div>
                  <Slider value={[fadeIn]} onValueChange={(v) => setFadeIn(v[0])} min={0} max={3} step={0.1} data-testid="editor-fade-in" />
                </div>
                <div>
                  <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                    <span className="flex items-center gap-1.5"><Blend className="h-3 w-3 text-nexus-pink" /> Fade out</span>
                    <span className="tabular-nums">{fadeOut.toFixed(1)}s</span>
                  </div>
                  <Slider value={[fadeOut]} onValueChange={(v) => setFadeOut(v[0])} min={0} max={3} step={0.1} data-testid="editor-fade-out" />
                </div>
                <div>
                  <button
                    onClick={splitAtPlayhead}
                    data-testid="editor-split-btn"
                    className="w-full h-10 rounded-full text-sm font-semibold border border-white/10 text-white hover:border-white/25 flex items-center justify-center gap-2"
                  >
                    <Split className="h-4 w-4" /> Split at playhead
                  </button>
                  <p className="text-[11px] text-zinc-500 mt-2">
                    Cuts the active clip in two at the playhead, TikTok-style. Both halves stay in the strip — reorder or delete them independently.
                  </p>
                </div>
              </div>
            )}

            {activeTool === "background" && kind === "video" && (
              <div className="space-y-5" data-testid="editor-panel-background">
                <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-500/15 via-nexus-blue/5 to-transparent border border-white/10">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Wand2 className="h-4 w-4 text-emerald-300" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-display font-bold text-white leading-tight">Green-screen removal</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Chroma key · Instagram / TikTok style</div>
                    </div>
                    <button
                      onClick={() => setChromaEnabled((v) => !v)}
                      className={`px-3 h-7 rounded-full text-[10px] uppercase tracking-widest font-bold ${chromaEnabled ? "nexus-gradient text-white" : "bg-white/5 text-zinc-400 border border-white/10"}`}
                      data-testid="editor-bg-toggle"
                    >
                      {chromaEnabled ? "ON" : "OFF"}
                    </button>
                  </div>
                  {chromaEnabled && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Key colour (the colour to remove)</div>
                        <div className="flex gap-2 mb-2">
                          {["#00FF00", "#00CC00", "#0000FF", "#FF00FF"].map((c) => (
                            <button
                              key={c}
                              onClick={() => setChromaColor(c)}
                              className={`h-10 flex-1 rounded-lg border-2 transition-all ${chromaColor.toLowerCase() === c.toLowerCase() ? "border-white" : "border-white/10"}`}
                              style={{ background: c }}
                              data-testid={`editor-bg-key-${c.replace("#", "")}`}
                              aria-label={`Set key colour ${c}`}
                            />
                          ))}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={chromaColor}
                            onChange={(e) => setChromaColor(e.target.value)}
                            className="h-9 w-12 rounded-lg border border-white/10 bg-transparent cursor-pointer"
                            data-testid="editor-bg-key-picker"
                          />
                          <span className="text-[11px] font-mono text-zinc-400 uppercase">{chromaColor}</span>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                          <span>Similarity</span>
                          <span className="tabular-nums">{(chromaSimilarity * 100).toFixed(0)}%</span>
                        </div>
                        <Slider value={[chromaSimilarity]} onValueChange={(v) => setChromaSimilarity(v[0])} min={0.01} max={0.5} step={0.01} data-testid="editor-bg-similarity" />
                        <p className="text-[10px] text-zinc-500 mt-1">Higher = removes a wider colour range. Start ~30% and adjust.</p>
                      </div>
                      <div>
                        <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                          <span>Edge softness</span>
                          <span className="tabular-nums">{(chromaBlend * 100).toFixed(0)}%</span>
                        </div>
                        <Slider value={[chromaBlend]} onValueChange={(v) => setChromaBlend(v[0])} min={0} max={0.3} step={0.01} data-testid="editor-bg-blend" />
                        <p className="text-[10px] text-zinc-500 mt-1">Adds a soft alpha ramp around the keyed edges.</p>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Preview background</div>
                        <div className="flex gap-2 mb-2">
                          {["#111111", "#FFFFFF", "#FF2F6E", "#8B5CF6", "#3B82F6", "#10B981"].map((c) => (
                            <button
                              key={c}
                              onClick={() => setChromaBgColor(c)}
                              className={`h-8 flex-1 rounded-lg border-2 transition-all ${chromaBgColor.toLowerCase() === c.toLowerCase() ? "border-white" : "border-white/10"}`}
                              style={{ background: c }}
                              data-testid={`editor-bg-preview-${c.replace("#", "")}`}
                              aria-label={`Preview background ${c}`}
                            />
                          ))}
                        </div>
                        <input
                          type="color"
                          value={chromaBgColor}
                          onChange={(e) => setChromaBgColor(e.target.value)}
                          className="h-9 w-full rounded-lg border border-white/10 bg-transparent cursor-pointer"
                          data-testid="editor-bg-preview-picker"
                        />
                      </div>
                      <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                        <strong>Note:</strong> exported MP4 renders keyed pixels as black (H.264 has no alpha). Coloured backgrounds are preview-only. Full compositing lands in Studio v4.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTool === "audio" && kind === "video" && (
              <div className="space-y-5">
                {/* Original clip audio */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Volume2 className="h-3 w-3 text-nexus-blue" /> Original clip audio</div>
                  <button
                    onClick={() => setMuted(!muted)}
                    className={`w-full py-3 rounded-xl text-sm font-semibold border transition-colors flex items-center justify-center gap-2 ${muted ? "bg-red-500/15 text-red-100 border-red-500/40" : "bg-white/5 text-white border-white/10 hover:bg-white/10"}`}
                    data-testid="editor-mute-toggle"
                  >
                    {muted ? <><VolumeX className="h-4 w-4" /> Muted on export</> : <><Volume2 className="h-4 w-4" /> Keep original audio</>}
                  </button>
                  {!muted && (
                    <div className="mt-3">
                      <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                        <span>Volume</span>
                        <span className="tabular-nums">{Math.round(volume * 100)}%</span>
                      </div>
                      <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} min={0} max={2} step={0.05} data-testid="editor-volume" />
                    </div>
                  )}
                </div>

                {/* Music track — background music mixing */}
                <div className="rounded-2xl p-4 bg-gradient-to-br from-nexus-blue/10 via-fuchsia-500/5 to-nexus-pink/10 border border-white/10" data-testid="editor-music-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-lg nexus-gradient flex items-center justify-center">
                      <Music className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-display font-bold text-white leading-tight">Music track</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Nexus Beats library + upload</div>
                    </div>
                    {musicFile && (
                      <button onClick={clearMusic} className="text-zinc-500 hover:text-red-300 text-[10px] uppercase tracking-widest font-semibold" data-testid="editor-music-clear">
                        Remove
                      </button>
                    )}
                  </div>

                  <input
                    ref={musicFileInputRef}
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/aac,audio/ogg,audio/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) loadMusicFile(f); }}
                    data-testid="editor-music-input"
                  />

                  {!musicFile ? (
                    <>
                      {/* Tab switcher */}
                      <div className={`grid ${proAvailable ? "grid-cols-3" : "grid-cols-2"} gap-1 p-1 rounded-full bg-black/40 border border-white/10 mb-3`}>
                        <button
                          onClick={() => { setMusicTab("library"); fetchMusicLibrary(); }}
                          className={`h-8 rounded-full text-[10px] font-semibold uppercase tracking-widest transition-colors ${musicTab === "library" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
                          data-testid="editor-music-tab-library"
                        >Library</button>
                        {proAvailable && (
                          <button
                            onClick={() => { setMusicTab("pro"); if (proTracks.length === 0) searchPro(); }}
                            className={`h-8 rounded-full text-[10px] font-semibold uppercase tracking-widest transition-colors flex items-center justify-center gap-1 ${musicTab === "pro" ? "nexus-gradient text-white" : "text-zinc-400 hover:text-white"}`}
                            data-testid="editor-music-tab-pro"
                          >
                            <Sparkles className="h-3 w-3" /> Pro
                          </button>
                        )}
                        <button
                          onClick={() => setMusicTab("upload")}
                          className={`h-8 rounded-full text-[10px] font-semibold uppercase tracking-widest transition-colors ${musicTab === "upload" ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}
                          data-testid="editor-music-tab-upload"
                        >Upload</button>
                      </div>

                      {musicTab === "pro" && (
                        <div className="space-y-2" data-testid="editor-pro-music">
                          <audio ref={proPreviewRef} onEnded={() => setProPreviewingId(null)} className="hidden" />
                          {/* Search + mood filters */}
                          <div className="flex gap-1.5">
                            <Input
                              value={proSearchTerm}
                              onChange={(e) => setProSearchTerm(e.target.value.slice(0, 100))}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchPro(); } }}
                              placeholder="e.g. sunset drive, workout hype…"
                              className="bg-white/[0.03] border-white/10 text-white text-sm flex-1"
                              data-testid="editor-pro-search-input"
                            />
                            <Button
                              onClick={searchPro}
                              disabled={proLoading}
                              className="rounded-full h-10 px-4 bg-white/10 hover:bg-white/15 text-white border-0 disabled:opacity-50"
                              data-testid="editor-pro-search-btn"
                            >
                              {proLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                            </Button>
                          </div>
                          {proMoods.length > 0 && (
                            <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1">
                              <button
                                onClick={() => { setProSelectedMood(null); }}
                                className={`shrink-0 h-7 px-2.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${!proSelectedMood ? "bg-white text-black border-white" : "bg-white/[0.03] border-white/10 text-zinc-400 hover:text-white"}`}
                              >All</button>
                              {proMoods.slice(0, 12).map((m) => (
                                <button
                                  key={m.id}
                                  onClick={() => setProSelectedMood(m.id)}
                                  className={`shrink-0 h-7 px-2.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${proSelectedMood === m.id ? "bg-nexus-pink text-white border-nexus-pink" : "bg-white/[0.03] border-white/10 text-zinc-400 hover:text-white"}`}
                                  data-testid={`editor-pro-mood-${m.id}`}
                                >{m.name}</button>
                              ))}
                            </div>
                          )}
                          {/* Track cards */}
                          {proLoading && proTracks.length === 0 && (
                            <div className="text-center py-6 text-zinc-500 text-xs flex flex-col items-center gap-2">
                              <Loader2 className="h-5 w-5 animate-spin" /> Searching Epidemic Sound…
                            </div>
                          )}
                          {proTracks.length > 0 && (
                            <div className="grid grid-cols-1 gap-1.5 max-h-72 overflow-y-auto pr-1">
                              {proTracks.map((tr) => (
                                <div
                                  key={tr.id}
                                  className="p-2 rounded-xl bg-white/[0.03] border border-white/10 hover:border-nexus-pink/40 transition-colors flex items-center gap-2"
                                  data-testid={`editor-pro-track-${tr.id}`}
                                >
                                  <button
                                    onClick={() => toggleProPreview(tr)}
                                    className="relative h-11 w-11 rounded-lg overflow-hidden border border-white/10 shrink-0 group"
                                    title="Preview"
                                  >
                                    {tr.cover_url ? (
                                      <img src={tr.cover_url} alt="" className="h-full w-full object-cover" />
                                    ) : <div className="h-full w-full bg-black/60" />}
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-90 group-hover:bg-black/60">
                                      {proPreviewingId === tr.id ? <span className="text-white text-xs">▐▐</span> : <span className="text-white text-sm ml-0.5">▶</span>}
                                    </div>
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold text-white truncate">{tr.title}</div>
                                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest truncate">
                                      {(tr.artists || []).join(", ")} · {tr.bpm || "-"} BPM · {tr.duration_s || "-"}s
                                    </div>
                                  </div>
                                  <Button
                                    onClick={() => pickProTrack(tr)}
                                    disabled={proPickingId === tr.id}
                                    className="nexus-gradient text-white border-0 rounded-full h-8 px-3 text-[10px] uppercase tracking-widest font-bold disabled:opacity-60"
                                    data-testid={`editor-pro-track-${tr.id}-add`}
                                  >
                                    {proPickingId === tr.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Use"}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          {proTracks.length === 0 && !proLoading && (
                            <div className="text-center py-4 text-zinc-500 text-xs">
                              Search Epidemic Sound&apos;s 55k+ track catalog
                            </div>
                          )}
                          <p className="text-[10px] text-zinc-500 pt-1 flex items-center gap-1">
                            <Sparkles className="h-2.5 w-2.5 text-nexus-pink" />
                            Powered by Epidemic Sound · commercial licence via Nexus Pro
                          </p>
                        </div>
                      )}

                      {musicTab === "library" && (
                        <div className="space-y-2" data-testid="editor-music-library">
                          {libraryLoading && musicLibrary.length === 0 && (
                            <div className="text-center py-6 text-zinc-500 text-xs flex flex-col items-center gap-2">
                              <Loader2 className="h-5 w-5 animate-spin" /> Loading Nexus Beats…
                            </div>
                          )}
                          {musicLibrary.length === 0 && !libraryLoading && (
                            <button
                              onClick={fetchMusicLibrary}
                              className="w-full py-3 rounded-xl border border-dashed border-white/20 text-zinc-300 text-xs font-semibold hover:border-white/40 hover:text-white"
                              data-testid="editor-music-library-load"
                            >
                              Load Nexus Beats
                            </button>
                          )}
                          {musicLibrary.length > 0 && (
                            <>
                              <div className="grid grid-cols-1 gap-1.5 max-h-64 overflow-y-auto pr-1">
                                {musicLibrary.map((tr) => (
                                  <button
                                    key={tr.id}
                                    onClick={() => pickLibraryTrack(tr)}
                                    disabled={libraryPickingId === tr.id}
                                    className="text-left p-2.5 rounded-xl bg-white/[0.03] border border-white/10 hover:border-nexus-pink/40 hover:bg-white/[0.06] transition-colors flex items-center gap-2 disabled:opacity-60"
                                    data-testid={`editor-music-track-${tr.id}`}
                                  >
                                    <div className="h-9 w-9 rounded-lg bg-black/60 border border-white/10 flex items-center justify-center shrink-0">
                                      {libraryPickingId === tr.id ? <Loader2 className="h-4 w-4 animate-spin text-nexus-pink" /> : <Music className="h-4 w-4 text-nexus-blue" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-semibold text-white truncate">{tr.title}</div>
                                      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{tr.mood} · {tr.bpm} BPM · {tr.duration_s}s</div>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest text-nexus-pink font-semibold">Use</span>
                                  </button>
                                ))}
                              </div>
                              <p className="text-[10px] text-zinc-500 pt-1">Nexus Beats · CC0 synth loops. For real tracks, upload your own.</p>
                            </>
                          )}
                          {/* Pro Music CTA — switch to Pro tab for Epidemic Sound */}
                          {proAvailable && (
                            <button
                              onClick={() => { setMusicTab("pro"); if (proTracks.length === 0) searchPro(); }}
                              className="mt-3 w-full p-3 rounded-xl border border-nexus-pink/30 bg-nexus-pink/[0.05] hover:bg-nexus-pink/[0.1] transition-colors text-left"
                              data-testid="editor-music-jump-to-pro"
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <Sparkles className="h-3.5 w-3.5 text-nexus-pink" />
                                <span className="text-[10px] uppercase tracking-widest text-nexus-pink font-bold">Try Pro Music →</span>
                              </div>
                              <p className="text-[11px] text-zinc-400">
                                55,000+ chart-friendly tracks from Epidemic Sound — searchable by mood or vibe. Fully licensed for commercial use.
                              </p>
                            </button>
                          )}
                        </div>
                      )}

                      {musicTab === "upload" && (
                        <div className="space-y-2">
                          <Button
                            onClick={() => musicFileInputRef.current?.click()}
                            className="nexus-gradient text-white border-0 rounded-full h-10 w-full font-semibold"
                            data-testid="editor-music-upload"
                          >
                            <Music className="h-4 w-4 mr-2" /> Upload a track
                          </Button>
                          <p className="text-[10px] text-zinc-500 text-center">MP3 / WAV / M4A · up to 20 MB · your file, your rights</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Preview player */}
                      <audio ref={musicAudioRef} src={musicUrl} onEnded={() => setMusicPreviewing(false)} className="hidden" />
                      <div className="flex items-center gap-2 mb-3 p-2 rounded-xl bg-black/40 border border-white/10">
                        <button
                          onClick={toggleMusicPreview}
                          className="h-8 w-8 rounded-full nexus-gradient flex items-center justify-center text-white shrink-0"
                          data-testid="editor-music-preview"
                          title={musicPreviewing ? "Pause preview" : "Play preview"}
                        >
                          {musicPreviewing ? <span className="text-sm leading-none">▐▐</span> : <span className="text-sm leading-none ml-0.5">▶</span>}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white truncate" data-testid="editor-music-name">{musicName}</div>
                          <div className="text-[10px] text-zinc-500">Preview only — export mixes at chosen settings</div>
                        </div>
                      </div>

                      {/* Music volume */}
                      <div className="mb-3">
                        <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                          <span>Music volume</span>
                          <span className="tabular-nums">{Math.round(musicVolume * 100)}%</span>
                        </div>
                        <Slider value={[musicVolume]} onValueChange={(v) => setMusicVolume(v[0])} min={0} max={2} step={0.05} data-testid="editor-music-volume" />
                      </div>

                      {/* Music start offset */}
                      <div className="mb-3">
                        <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                          <span>Start from</span>
                          <span className="tabular-nums">{musicStart.toFixed(1)}s into track</span>
                        </div>
                        <Slider value={[musicStart]} onValueChange={(v) => setMusicStart(v[0])} min={0} max={60} step={0.5} data-testid="editor-music-start" />
                      </div>

                      {/* Fades */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                            <span>Fade in</span>
                            <span className="tabular-nums">{musicFadeIn.toFixed(1)}s</span>
                          </div>
                          <Slider value={[musicFadeIn]} onValueChange={(v) => setMusicFadeIn(v[0])} min={0} max={3} step={0.1} data-testid="editor-music-fadein" />
                        </div>
                        <div>
                          <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                            <span>Fade out</span>
                            <span className="tabular-nums">{musicFadeOut.toFixed(1)}s</span>
                          </div>
                          <Slider value={[musicFadeOut]} onValueChange={(v) => setMusicFadeOut(v[0])} min={0} max={3} step={0.1} data-testid="editor-music-fadeout" />
                        </div>
                      </div>

                      {/* Duck original */}
                      <button
                        onClick={() => setMusicDuck(!musicDuck)}
                        className={`w-full h-10 rounded-full text-[11px] font-semibold border flex items-center justify-center gap-2 ${musicDuck ? "bg-nexus-blue/15 text-white border-nexus-blue/40" : "bg-white/[0.03] text-zinc-300 border-white/10 hover:border-white/25"}`}
                        data-testid="editor-music-duck"
                      >
                        {musicDuck
                          ? <><Volume2 className="h-3.5 w-3.5" /> Auto-duck original audio to 30%</>
                          : <><VolumeX className="h-3.5 w-3.5" /> Keep both at full volume</>}
                      </button>
                    </>
                  )}
                </div>

                {/* Voiceover track (multi-track audio) */}
                <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-500/10 via-nexus-blue/5 to-transparent border border-white/10" data-testid="editor-voiceover-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-lg bg-emerald-500/80 flex items-center justify-center">
                      <Mic className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-display font-bold text-white leading-tight">Voiceover</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Layer on top of clip + music</div>
                    </div>
                    {voiceoverFile && (
                      <button onClick={clearVoiceover} className="text-zinc-500 hover:text-red-300 text-[10px] uppercase tracking-widest font-semibold" data-testid="editor-voiceover-clear">
                        Remove
                      </button>
                    )}
                  </div>

                  <input
                    ref={voiceoverFileInputRef}
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/aac,audio/ogg,audio/*"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) loadVoiceoverFile(f); }}
                    data-testid="editor-voiceover-input"
                  />

                  {!voiceoverFile ? (
                    <div className="space-y-2">
                      <Button
                        onClick={() => voiceoverFileInputRef.current?.click()}
                        className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 rounded-full h-10 w-full font-semibold"
                        data-testid="editor-voiceover-upload"
                      >
                        <Mic className="h-4 w-4 mr-2" /> Add voiceover
                      </Button>
                      <p className="text-[10px] text-zinc-500 text-center">Perfect for tutorials, reactions, and narration · up to 15 MB</p>
                    </div>
                  ) : (
                    <>
                      <audio ref={voiceoverAudioRef} src={voiceoverUrl} onEnded={() => setVoiceoverPreviewing(false)} className="hidden" />
                      <div className="flex items-center gap-2 mb-3 p-2 rounded-xl bg-black/40 border border-white/10">
                        <button
                          onClick={toggleVoiceoverPreview}
                          className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center text-white shrink-0"
                          data-testid="editor-voiceover-preview"
                          title={voiceoverPreviewing ? "Pause preview" : "Play preview"}
                        >
                          {voiceoverPreviewing ? <span className="text-sm leading-none">▐▐</span> : <span className="text-sm leading-none ml-0.5">▶</span>}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-white truncate" data-testid="editor-voiceover-name">{voiceoverName}</div>
                          <div className="text-[10px] text-zinc-500">Preview only — export mixes on top of everything else</div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                          <span>Voiceover volume</span>
                          <span className="tabular-nums">{Math.round(voiceoverVolume * 100)}%</span>
                        </div>
                        <Slider value={[voiceoverVolume]} onValueChange={(v) => setVoiceoverVolume(v[0])} min={0} max={2} step={0.05} data-testid="editor-voiceover-volume" />
                      </div>

                      <div>
                        <div className="flex justify-between text-[11px] text-zinc-400 mb-1">
                          <span>Start voiceover at</span>
                          <span className="tabular-nums">{voiceoverStart.toFixed(1)}s into edit</span>
                        </div>
                        <Slider value={[voiceoverStart]} onValueChange={(v) => setVoiceoverStart(v[0])} min={0} max={Math.max(1, totalEditDuration())} step={0.1} data-testid="editor-voiceover-start" />
                      </div>
                    </>
                  )}
                </div>

                {/* Sound FX library */}
                <div className="rounded-2xl p-4 bg-gradient-to-br from-amber-500/10 via-nexus-pink/5 to-transparent border border-white/10" data-testid="editor-fx-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-lg bg-amber-500/80 flex items-center justify-center">
                      <Zap className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-display font-bold text-white leading-tight">Sound FX</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Whooshes, boings, dings, risers</div>
                    </div>
                    <span className="text-[10px] text-zinc-500 tabular-nums">{fxSlots.length}/4</span>
                  </div>
                  <audio ref={fxAudioPreviewRef} className="hidden" />

                  {fxLibrary.length === 0 && !fxLoading && (
                    <button
                      onClick={fetchFxLibrary}
                      className="w-full py-3 rounded-xl border border-dashed border-white/20 text-zinc-300 text-xs font-semibold hover:border-white/40 hover:text-white"
                      data-testid="editor-fx-load"
                    >
                      Load sound FX library
                    </button>
                  )}
                  {fxLoading && (
                    <div className="text-center py-4 text-zinc-500 text-xs flex flex-col items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading FX…
                    </div>
                  )}
                  {fxLibrary.length > 0 && (
                    <div className="grid grid-cols-4 gap-1.5 mb-3">
                      {fxLibrary.map((fx) => (
                        <button
                          key={fx.id}
                          onClick={() => addFxSlot(fx)}
                          onMouseEnter={() => previewFx(fx)}
                          onFocus={() => previewFx(fx)}
                          disabled={fxPickingId === fx.id || fxSlots.length >= 4}
                          className="p-2 rounded-xl bg-white/[0.03] border border-white/10 hover:border-amber-500/40 hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                          data-testid={`editor-fx-${fx.id}`}
                          title={`${fx.title} · hover to preview`}
                        >
                          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{fx.title}</div>
                          <div className="text-[9px] text-zinc-500 tabular-nums mt-0.5">{fx.duration_ms}ms</div>
                        </button>
                      ))}
                    </div>
                  )}

                  {fxSlots.length > 0 && (
                    <div className="space-y-2 pt-2 border-t border-white/5">
                      {fxSlots.map((s, i) => (
                        <div key={s.id} className="p-2 rounded-xl bg-black/40 border border-white/10 space-y-2" data-testid={`fx-slot-${s.id}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold shrink-0">#{i + 1} {s.title}</span>
                            <span className="text-[10px] text-zinc-400 tabular-nums ml-auto">@ {s.atSec.toFixed(1)}s</span>
                            <button onClick={() => removeFxSlot(s.id)} className="text-zinc-500 hover:text-red-300" data-testid={`fx-remove-${s.id}`}>
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1">Trigger at</div>
                            <Slider value={[s.atSec]} onValueChange={(v) => updateFxSlot(s.id, { atSec: v[0] })} min={0} max={Math.max(0.5, totalEditDuration())} step={0.1} />
                          </div>
                          <div>
                            <div className="flex justify-between text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
                              <span>Volume</span><span className="tabular-nums normal-case tracking-normal">{Math.round(s.volume * 100)}%</span>
                            </div>
                            <Slider value={[s.volume]} onValueChange={(v) => updateFxSlot(s.id, { volume: v[0] })} min={0} max={2} step={0.05} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Beat sync — available for any music track when multi-clip is active */}
                {(activeMusicTrackId || musicFile) && extraClips.length > 0 && (
                  <div className="rounded-2xl p-4 bg-gradient-to-br from-nexus-pink/10 via-fuchsia-500/5 to-nexus-blue/10 border border-nexus-pink/30" data-testid="editor-beat-sync-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-8 w-8 rounded-lg bg-nexus-pink flex items-center justify-center">
                        <Zap className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-display font-bold text-white leading-tight">Beat sync</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                          {activeMusicTrackId ? "Nexus Beats · beat grid" : "Uploaded track · onset detection"}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={snapClipsToBeats}
                      disabled={beatSyncing}
                      className="nexus-gradient text-white border-0 rounded-full h-10 w-full font-semibold disabled:opacity-60"
                      data-testid="editor-beat-sync-btn"
                    >
                      {beatSyncing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Snapping…</> : <><Zap className="h-4 w-4 mr-2" /> Snap {1 + extraClips.length} clips to beat</>}
                    </Button>
                    <p className="text-[10px] text-zinc-500 mt-2 text-center">Auto-adjusts trim points + sets transitions to fade for a musical feel.</p>
                  </div>
                )}
              </div>
            )}

            {activeTool === "ai" && (
              <div className="space-y-5">
                {/* AI Auto-Editor — Claude produces full multi-clip edit in one tap */}
                {kind === "video" && (
                  <div className="rounded-2xl p-4 bg-gradient-to-br from-nexus-pink/15 via-nexus-blue/10 to-emerald-500/10 border border-nexus-pink/30" data-testid="editor-auto-edit">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-8 w-8 rounded-lg nexus-gradient flex items-center justify-center">
                        <Wand2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm font-display font-bold text-white leading-tight">Auto-Edit</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Full edit in one tap · filters + transitions + text + music
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={runAutoEdit}
                      disabled={autoEditBusy}
                      className="nexus-gradient text-white border-0 rounded-full h-11 w-full font-bold disabled:opacity-60 text-sm"
                      data-testid="editor-auto-edit-btn"
                    >
                      {autoEditBusy
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Editing your clips…</>
                        : <><Sparkles className="h-4 w-4 mr-2" /> Auto-Edit {1 + Math.min(extraClips.length, 2)} clip{extraClips.length ? "s" : ""}</>}
                    </Button>
                    {autoEditResult && (
                      <div className="mt-3 p-3 rounded-xl bg-black/40 hairline text-xs space-y-1" data-testid="editor-auto-edit-result">
                        <div className="text-white/90 italic">&ldquo;{autoEditResult.vibe}&rdquo;</div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Music mood: <span className="text-nexus-pink font-semibold">{autoEditResult.music || "—"}</span>
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-zinc-500 mt-2">
                      Claude picks per-clip filters, transitions, an on-brand headline, and a matching Nexus Beats track — then beat-syncs. Adjust anything after.
                    </p>
                  </div>
                )}

                {/* Smart Suggest — vision-based edit recommendations */}
                <div className="rounded-2xl p-4 bg-gradient-to-br from-nexus-pink/10 via-fuchsia-500/5 to-nexus-blue/10 border border-white/10" data-testid="editor-smart-suggest">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-8 w-8 rounded-lg nexus-gradient flex items-center justify-center">
                      <Sparkles className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <div className="text-sm font-display font-bold text-white leading-tight">Smart Suggest</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Claude reads the frame → picks a vibe</div>
                    </div>
                  </div>
                  <div className={`grid gap-2 ${extraClips.length > 0 ? "grid-cols-2" : "grid-cols-1"}`}>
                    <Button
                      onClick={() => runSmartSuggest({ perClip: false })}
                      disabled={suggestBusy}
                      className="nexus-gradient text-white border-0 rounded-full h-10 font-semibold disabled:opacity-60"
                      data-testid="editor-smart-suggest-btn"
                    >
                      {suggestBusy
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading…</>
                        : <><Wand2 className="h-4 w-4 mr-2" /> Suggest {isActivePrimary ? "Main" : "this clip"}</>}
                    </Button>
                    {extraClips.length > 0 && (
                      <Button
                        onClick={() => runSmartSuggest({ perClip: true })}
                        disabled={suggestBusy}
                        variant="outline"
                        className="rounded-full h-10 border-white/10 text-white hover:bg-white/5 font-semibold disabled:opacity-60"
                        data-testid="editor-smart-suggest-all-btn"
                      >
                        {suggestBusy
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading…</>
                          : <><Sparkles className="h-4 w-4 mr-2" /> Analyze each clip</>}
                      </Button>
                    )}
                  </div>
                  {suggestResult && (
                    <div className="mt-3 p-3 rounded-xl bg-white/[0.03] hairline text-xs space-y-1" data-testid="editor-smart-suggest-result">
                      <div className="text-white/90 italic">&ldquo;{suggestResult.vibe}&rdquo;</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {suggestResult.refined ? "Refined" : "Applied"} to {suggestResult.appliedTo}
                      </div>
                      {(suggestResult.results || []).length > 1 && (
                        <div className="pt-2 border-t border-white/5 space-y-0.5">
                          {suggestResult.results.map((r, i) => (
                            <div key={r.id} className="flex items-center justify-between text-[11px] text-zinc-300">
                              <span>{r.id === "primary" ? "Main" : `Clip ${i + 1}`}</span>
                              <span className="text-nexus-pink font-semibold">{filterLabel(r.filter)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Vibe refine chat — free-form iterations on the current look */}
                  <div className="mt-3 pt-3 border-t border-white/5">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                      <MessageCircle className="h-3 w-3 text-nexus-blue" /> Refine the vibe
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        value={refinePrompt}
                        onChange={(e) => setRefinePrompt(e.target.value.slice(0, 200))}
                        placeholder='e.g. "make it colder", "more cinematic"…'
                        className="bg-white/[0.03] border-white/10 text-white text-sm flex-1"
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSmartSuggest({ refine: true }); } }}
                        data-testid="editor-refine-input"
                      />
                      <Button
                        onClick={() => runSmartSuggest({ refine: true })}
                        disabled={suggestBusy || !refinePrompt.trim()}
                        className="rounded-full h-10 px-4 bg-white/10 hover:bg-white/15 text-white border-0 disabled:opacity-50"
                        data-testid="editor-refine-btn"
                      >
                        {suggestBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refine"}
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {["make it colder", "more cinematic", "warmer + moodier", "extra punchy", "add a hashtag"].map((p) => (
                        <button
                          key={p}
                          onClick={() => { setRefinePrompt(p); }}
                          disabled={suggestBusy}
                          className="text-[10px] px-2 py-1 rounded-full border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 disabled:opacity-40"
                          data-testid={`editor-refine-quick-${p.replace(/\s+/g, "-")}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Wand2 className="h-3 w-3 text-nexus-pink" /> Claude caption</div>
                  <Input
                    value={captionContext}
                    onChange={(e) => setCaptionContext(e.target.value)}
                    placeholder="Describe the clip (optional)"
                    className="bg-white/[0.03] border-white/10 text-white mb-2"
                    data-testid="editor-caption-context"
                  />
                  <Button onClick={generateCaption} disabled={captionBusy} className="nexus-gradient text-white border-0 rounded-full w-full h-10" data-testid="editor-caption-btn">
                    {captionBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Writing…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate caption + hashtags</>}
                  </Button>
                  {caption && (
                    <div className="mt-3 p-3 rounded-xl bg-white/[0.03] hairline text-sm space-y-2" data-testid="editor-caption-result">
                      <div className="text-white/90">{caption.caption}</div>
                      <div className="text-[11px] text-nexus-blue break-words">{(caption.hashtags || []).join(" ")}</div>
                      {(caption.hooks || []).length > 0 && (
                        <div className="pt-2 border-t border-white/5">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Alt hooks</div>
                          {caption.hooks.map((h, i) => <div key={i} className="text-xs text-zinc-300">{h}</div>)}
                        </div>
                      )}
                      <button onClick={copyCaption} className="text-[11px] uppercase tracking-widest text-nexus-pink hover:text-white flex items-center gap-1" data-testid="editor-caption-copy">
                        <Copy className="h-3 w-3" /> Copy all
                      </button>
                    </div>
                  )}
                </div>
                {kind === "video" && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Captions className="h-3 w-3 text-nexus-blue" /> Auto-subtitles</div>
                    <Button onClick={generateSubs} disabled={subsBusy} variant="outline" className="rounded-full w-full h-10 border-white/10 text-white hover:bg-white/5" data-testid="editor-subs-btn">
                      {subsBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Transcribing…</> : <><Captions className="h-4 w-4 mr-2" /> {subs ? "Regenerate subtitles" : "Generate subtitles"}</>}
                    </Button>
                    {subs && (
                      <div className="mt-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-[11px] text-emerald-100" data-testid="editor-subs-result">
                        ✓ Burns onto the video on export.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------- Bottom dock ---------- */}
      <div className="border-t border-white/5 bg-[#050507] flex-shrink-0" data-testid="editor-dock">
        <div className="flex items-center justify-around overflow-x-auto no-scrollbar px-2 py-2 gap-1">
          {dock.map((d) => {
            const Icon = d.icon;
            const on = activeTool === d.v;
            return (
              <button
                key={d.v}
                onClick={() => setActiveTool(on ? null : d.v)}
                className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-xl min-w-[62px] transition-colors ${
                  on ? "bg-white/10 text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid={`editor-tab-${d.v}`}
              >
                <Icon className={`h-5 w-5 ${on ? "text-nexus-pink" : ""}`} />
                <span className="text-[10px] font-semibold tracking-wide">{d.l}</span>
              </button>
            );
          })}
        </div>
      </div>

      {showTour && <StudioOnboarding mediaKind={kind} onClose={() => setShowTour(false)} />}

      {/* Draft auto-recovery — offered once when opening a fresh editor with a recent draft */}
      {recoverableDraft && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" data-testid="editor-recovery-modal">
          <div className="relative w-full max-w-md rounded-3xl bg-[#0A0A10] border border-white/10 p-6 shadow-[0_30px_120px_rgba(0,0,0,0.7)]">
            <div className="h-12 w-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-4">
              <Save className="h-6 w-6 text-emerald-300" />
            </div>
            <h2 className="font-display font-bold text-2xl text-white leading-tight">Continue your last edit?</h2>
            <p className="text-sm text-zinc-400 mt-2">
              We auto-saved <span className="text-white font-semibold">{(recoverableDraft.title || "your draft").slice(0, 40)}</span> {timeAgo(recoverableDraft.updated_at)}. Restore it now?
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => { onDraftSaved?.(recoverableDraft.id); setRecoverableDraft(null); toast.success("Restoring draft…"); }}
                className="nexus-gradient text-white border-0 rounded-full h-11 font-semibold flex-1"
                data-testid="editor-recovery-restore"
              >
                <Save className="h-4 w-4 mr-2" /> Restore draft
              </Button>
              <Button
                onClick={() => setRecoverableDraft(null)}
                variant="outline"
                className="rounded-full h-11 border-white/10 text-white hover:bg-white/5 font-semibold"
                data-testid="editor-recovery-dismiss"
              >
                Start fresh
              </Button>
            </div>
            <p className="text-[10px] text-zinc-600 mt-3 text-center">Your draft stays saved either way — you can pick it up from Studio drafts anytime.</p>
          </div>
        </div>
      )}

      {/* Upgrade modal — shown when quota is exhausted OR user clicks the quota chip */}
      {showUpgradeModal && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowUpgradeModal(false)} data-testid="editor-upgrade-modal">
          <div className="relative w-full max-w-md rounded-3xl bg-gradient-to-br from-[#0A0A10] via-[#12121A] to-[#0A0A10] border border-nexus-pink/30 p-6 sm:p-8 shadow-[0_30px_120px_rgba(255,47,110,0.25)]" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowUpgradeModal(false)} className="absolute top-3 right-3 h-8 w-8 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white" data-testid="editor-upgrade-close">
              <X className="h-4 w-4" />
            </button>
            <div className="h-14 w-14 rounded-2xl nexus-gradient flex items-center justify-center mb-5">
              <Sparkles className="h-7 w-7 text-white" />
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl text-white leading-tight">Go unlimited with<br/>Creator Pro</h2>
            <p className="text-sm text-zinc-400 mt-3">
              {quota && !quota.can_export
                ? "You've used all 5 free exports. Upgrade to keep editing — and lose the Nexus watermark."
                : `You've used ${quota?.used ?? 0} of ${quota?.limit ?? 5} free exports. Upgrade any time to remove the Nexus watermark and get unlimited edits.`}
            </p>
            <ul className="mt-5 space-y-2 text-sm text-zinc-200">
              <li className="flex items-start gap-2"><span className="text-nexus-pink mt-0.5">✓</span> Unlimited video exports</li>
              <li className="flex items-start gap-2"><span className="text-nexus-pink mt-0.5">✓</span> No Nexus watermark on your videos</li>
              <li className="flex items-start gap-2"><span className="text-nexus-pink mt-0.5">✓</span> Priority access to new Studio features</li>
              <li className="flex items-start gap-2"><span className="text-nexus-pink mt-0.5">✓</span> Cancel anytime</li>
            </ul>
            <Button
              onClick={startCreatorProCheckout}
              disabled={checkoutBusy}
              className="nexus-gradient text-white border-0 rounded-full h-12 w-full font-semibold mt-6 text-base disabled:opacity-60"
              data-testid="editor-upgrade-checkout"
            >
              {checkoutBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Opening checkout…</> : <><Sparkles className="h-4 w-4 mr-2" /> Upgrade to Creator Pro</>}
            </Button>
            <p className="text-[10px] text-zinc-500 text-center mt-3 uppercase tracking-widest">Secure checkout via Stripe</p>
          </div>
        </div>
      )}
    </div>
  ), document.body);
}

/** Small rounded segmented button used across every tool panel */
function PillButton({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`h-9 px-2.5 rounded-full text-[11px] font-semibold border transition-all whitespace-nowrap ${
        active
          ? "bg-white text-black border-white shadow-[0_4px_20px_rgba(255,255,255,0.15)]"
          : "border-white/10 text-zinc-300 hover:text-white hover:border-white/25"
      }`}
    >
      {children}
    </button>
  );
}

/** Live-preview badge for text stickers — matches the ffmpeg canvas render. */
function TextStickerBadge({ text, shape, color }) {
  const bg = color || "#FF2F6E";
  const base = {
    color: "#FFFFFF",
    fontWeight: 900,
    letterSpacing: "0.02em",
    padding: shape === "tag" ? "0.35em 0.9em 0.35em 1.4em" : "0.35em 0.9em",
    display: "inline-block",
    lineHeight: 1,
    filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.5))",
    whiteSpace: "nowrap",
  };
  if (shape === "banner") {
    return (
      <span style={{ ...base, background: `linear-gradient(90deg, ${bg}, #3B82F6)`, clipPath: "polygon(0 0, 100% 0, calc(100% - 0.4em) 100%, 0.4em 100%)" }}>
        {text}
      </span>
    );
  }
  if (shape === "tag") {
    return (
      <span style={{ ...base, background: bg, clipPath: "polygon(0.5em 0, 100% 0, 100% 100%, 0.5em 100%, 0 50%)", position: "relative" }}>
        <span style={{ position: "absolute", left: "0.55em", top: "50%", transform: "translateY(-50%)", width: "0.35em", height: "0.35em", background: "#0A0A10", borderRadius: "50%" }} />
        {text}
      </span>
    );
  }
  // chip
  return <span style={{ ...base, background: bg, borderRadius: "999px" }}>{text}</span>;
}

